// bookingdata.js — Central data + constants for all visa booking pages
// Usage: import { getVisaData, visaList, rewardsData } from './bookingdata';

// ─── REWARDS SYSTEM ────────────────────────────────────────────────────────

import { createElement } from "react";

// ─── PAGINATION CONSTANTS ──────────────────────────────────────────────────
export const REVIEWS_INITIAL = 4;
export const FAQS_INITIAL    = 4;

// ─── PROTECT FEE CONSTANTS ─────────────────────────────────────────────────
export const PROTECT_FEE  = 299;
export const PROTECT_ORIG = 499;

// ─── EXPRESS VISA FEES BY COUNTRY ──────────────────────────────────────────
// Priority processing surcharge — varies by country complexity & turnaround
export const EXPRESS_FEE_BY_COUNTRY = {
  uae:           2000,   // E-Visa, 4-day standard  express 2-day
  thailand:      1500,   // E-Visa, 5-day standard  express 2-day
  switzerland:   3500,   // Schengen, 15-day standard  express 7-day
  vietnam:       1200,   // E-Visa, 3-day standard  express 24-hr
  indonesia:     1000,   // VOA/E-Visa, 2-day standard  express same-day
  maldives:       800,   // Arrival doc, minimal complexity
  usa:           5000,   // DS-160, high complexity, interview required
  "hong-kong":   1800,   // HKSAR Visitor, 5-day standard  express 2-day
  egypt:         1500,   // E-Visa, 5-day standard  express 2-day
  malaysia:      1000,   // eNTRI, 24-hr standard  express same-day
  singapore:     2000,   // SVP, 7-day standard  express 3-day
  turkey:        1500,   // E-Visa, 3-day standard  express 24-hr
  france:        3500,   // Schengen, 15-day standard  express 7-day
  japan:         2500,   // Embassy, 10-day standard  express 5-day
  canada:        4000,   // TRV, complex, express 5-day
  australia:     4000,   // Subclass 600, complex, express 5-day
  "new-zealand": 3500,   // INZ 1017, express 5-day
  germany:       3500,   // Schengen, 15-day standard  express 7-day
  spain:         3500,   // Schengen, 15-day standard  express 7-day
  italy:         3500,   // Schengen, 15-day standard  express 7-day
};

// ─── COUNTRY-SPECIFIC VISA PROCESS JOURNEY ─────────────────────────────────
// No icons or emoji — plain numbered steps, each unique to the country's actual process
export const VISA_JOURNEY = {
  uae: [
    { step: "01", title: "Document Collection",    desc: "Gather your valid passport, recent passport-size photo, confirmed return flight, hotel booking, and 3-month bank statement.", tag: "Day 1" },
    { step: "02", title: "Online Application",      desc: "Submit all documents through our secure portal and pay the UAE government fee of ₹6,800. No other charges are required upfront.", tag: "Day 1" },
    { step: "03", title: "Specialist Verification", desc: "Our UAE visa team reviews every document for GDRFA compliance and resolves any inconsistencies before official submission.", tag: "Day 1–2" },
    { step: "04", title: "GDRFA Processing",        desc: "We submit your complete file to the General Directorate of Residency and Foreigners Affairs for official processing.", tag: "Day 2–3" },
    { step: "05", title: "E-Visa Delivery",         desc: "Your UAE e-Visa is emailed as a PDF. No passport submission needed — print it or save it digitally and you are ready to travel.", tag: "By Day 4" },
  ],
  thailand: [
    { step: "01", title: "Eligibility Confirmation", desc: "We confirm your passport has at least 6 months validity beyond your travel date and your purpose of visit meets Thai e-Visa requirements.", tag: "Day 1" },
    { step: "02", title: "Document Submission",      desc: "Upload your passport copy, photo, confirmed return ticket, and hotel bookings through our portal and pay the government fee.", tag: "Day 1" },
    { step: "03", title: "Document Review",           desc: "Our Thailand specialists verify all submissions — particularly flight and hotel confirmations that Thai Immigration scrutinises closely.", tag: "Day 1–2" },
    { step: "04", title: "Immigration Processing",   desc: "Your application is forwarded to Thailand's e-Visa system. Standard processing completes within 5 working days for Indian nationals.", tag: "Day 2–4" },
    { step: "05", title: "E-Visa Issued",             desc: "Your approved Thailand e-Visa arrives by email. Present it at the airport immigration counter — no physical passport submission required.", tag: "By Day 5" },
  ],
  switzerland: [
    { step: "01", title: "Document Preparation",      desc: "Compile your passport (valid 3 months beyond return), 2 photographs, return flight, full hotel itinerary, travel insurance of at least €30,000, and 6-month bank statement.", tag: "Week 1" },
    { step: "02", title: "Application Audit",          desc: "Our Schengen specialists conduct a thorough review of every document against Swiss Consulate requirements — a step that significantly reduces rejection risk.", tag: "Week 1" },
    { step: "03", title: "VFS Appointment Booking",   desc: "We book your VFS Global biometric appointment. Swiss consulate slots fill 3–4 weeks in advance — early booking is essential.", tag: "Week 1–2" },
    { step: "04", title: "Biometric Submission",       desc: "You attend the VFS centre to provide fingerprints and submit your physical passport. The appointment takes approximately 30 minutes.", tag: "Appointment day" },
    { step: "05", title: "Consulate Review",           desc: "The Swiss Consulate assesses your application, travel history, and financial standing. Standard processing takes 10–15 working days from biometric submission.", tag: "Week 2–4" },
    { step: "06", title: "Passport Returned",          desc: "Your passport with the Switzerland Schengen visa sticker is couriered to your registered address via VFS Global.", tag: "By Day 15" },
  ],
  vietnam: [
    { step: "01", title: "Document Assembly",      desc: "Prepare your passport with at least 6 months validity, a digital passport photo on a plain white background, and your intended travel dates.", tag: "Day 1" },
    { step: "02", title: "E-Visa Application",     desc: "We file your application on the official Vietnam e-Visa portal with your personal details and travel information. Government fee is paid at this stage.", tag: "Day 1" },
    { step: "03", title: "Photo Compliance Check", desc: "Vietnam Immigration enforces strict photo requirements. We verify your image meets exact specifications before final submission to avoid rejection.", tag: "Day 1" },
    { step: "04", title: "E-Visa Issued",           desc: "Your Vietnam e-Visa arrives by email, valid for 90 days from date of issue with a 30-day stay. Present it digitally or as a printout at the airport.", tag: "By Day 3" },
  ],
  indonesia: [
    { step: "01", title: "Visa Route Selection", desc: "We determine whether the B211A e-Visa (for stays up to 60 days) or Visa on Arrival at Bali or Jakarta airports best suits your travel plan.", tag: "Day 1" },
    { step: "02", title: "Application Filing",   desc: "Submit your passport copy, photo, return ticket, and accommodation details via our portal and pay the Indonesian government fee.", tag: "Day 1" },
    { step: "03", title: "Processing",            desc: "Indonesia's Directorate General of Immigration processes e-Visa applications within 24–48 hours for standard tourism requests.", tag: "Day 1–2" },
    { step: "04", title: "E-Visa Delivered",      desc: "Your Indonesia e-Visa is issued as a QR-code document. Present it at the dedicated e-Visa lane on arrival to receive your entry stamp.", tag: "By Day 2" },
  ],
  maldives: [
    { step: "01", title: "Pre-Arrival Documentation", desc: "Prepare your passport, confirmed return flight, resort or hotel booking, and proof of sufficient funds. Indian nationals receive a visa on arrival — no prior application needed.", tag: "Before travel" },
    { step: "02", title: "Arrival Visa Processing",    desc: "On arrival at Velana International Airport, immigration officers review your documents and issue a 30-day tourist visa at the counter within 15–20 minutes.", tag: "On arrival" },
    { step: "03", title: "Entry Stamp Issued",         desc: "A 30-day visa stamp is placed in your passport. Extensions up to 90 days can be applied for at Maldives Immigration if you decide to extend your stay.", tag: "Within 20 mins" },
  ],
  usa: [
    { step: "01", title: "DS-160 Form Completion",   desc: "The DS-160 is the mandatory US nonimmigrant visa application form. We assist in completing every section accurately — errors here can cause outright rejection.", tag: "Day 1–2" },
    { step: "02", title: "MRV Fee Payment",           desc: "Pay the non-refundable US visa application (MRV) fee of $185 through the US Embassy payment portal. This is required before scheduling your interview.", tag: "Day 2" },
    { step: "03", title: "Interview Appointment",     desc: "We book your visa interview at the nearest US Consulate in Mumbai, New Delhi, Chennai, Hyderabad, or Kolkata. Wait times range from 2 weeks to several months.", tag: "2–8 weeks out" },
    { step: "04", title: "Document Preparation",      desc: "Prepare your DS-160 confirmation, MRV fee receipt, passport, photo, 3-year ITR, bank statement, employment letter, and travel itinerary — all originals required.", tag: "Before interview" },
    { step: "05", title: "Consulate Interview",       desc: "Attend your interview at the US Consulate. The officer assesses your purpose of travel, ties to India, and financial standing. Most interviews last under 5 minutes.", tag: "Interview day" },
    { step: "06", title: "Administrative Processing", desc: "Some applications undergo additional processing (AP) after the interview — typically 3–5 weeks — before a final decision is issued.", tag: "1–6 weeks" },
    { step: "07", title: "Passport Delivery",         desc: "Your passport with the US B1/B2 visa stamp is returned via the US Embassy VFS courier system to your registered delivery address.", tag: "1–2 weeks post-approval" },
  ],
  "hong-kong": [
    { step: "01", title: "Document Preparation",       desc: "Prepare your passport with at least 1 month validity beyond your intended stay, a passport photo, hotel booking, and confirmed return or onward flight.", tag: "Day 1" },
    { step: "02", title: "Pre-Arrival Registration",   desc: "We file your Hong Kong pre-arrival registration with the HKSAR Immigration Department. Indian nationals require this registration for most visit purposes.", tag: "Day 1" },
    { step: "03", title: "Immigration Review",          desc: "The HKSAR Immigration Department reviews your application confirming legitimate travel purpose, accommodation details, and financial means.", tag: "Day 2–4" },
    { step: "04", title: "Approval Reference Issued",  desc: "An approval reference number is issued electronically. You must present this at airline check-in and again at Hong Kong airport immigration.", tag: "By Day 5" },
    { step: "05", title: "Entry Stamp at HKSAR",       desc: "Present your approval reference at immigration on arrival. The officer issues an entry stamp valid for up to 14 days for tourism purposes.", tag: "On arrival" },
  ],
  egypt: [
    { step: "01", title: "Portal Application",  desc: "Apply through the official Egypt e-Visa portal with your passport details, intended travel dates, and place of accommodation in Egypt.", tag: "Day 1" },
    { step: "02", title: "Document Upload",      desc: "Upload your passport bio-data page and a digital passport photo. We verify all submissions meet Egypt's portal format and size requirements before filing.", tag: "Day 1" },
    { step: "03", title: "E-Visa Processing",    desc: "Egypt's e-Visa portal processes standard applications within 3–5 business days. Express processing is available through the official portal at an additional fee.", tag: "Day 2–4" },
    { step: "04", title: "E-Visa Issued",        desc: "Your Egypt e-Visa is sent to your email as a PDF. Print and carry it alongside your passport — a physical printout is required at Cairo, Hurghada, and Sharm el-Sheikh airports.", tag: "By Day 5" },
  ],
  malaysia: [
    { step: "01", title: "eNTRI Eligibility",      desc: "Indian passport holders qualify for Malaysia's eNTRI — a simplified electronic travel registration for tourism stays of up to 15 days. Longer stays require a full tourist visa.", tag: "Day 1" },
    { step: "02", title: "Registration Filing",    desc: "We complete your eNTRI registration online with your passport details, travel dates, and Malaysia accommodation information. The process takes under 30 minutes.", tag: "Day 1" },
    { step: "03", title: "Approval Confirmation",  desc: "eNTRI approvals are issued within 24 hours. Your confirmation with a unique reference number is sent by email.", tag: "Within 24 hrs" },
    { step: "04", title: "Border Entry",           desc: "Present your eNTRI confirmation at Malaysia immigration. You receive a 15-day entry stamp. eNTRI is non-extendable — a full tourist visa is needed for longer stays.", tag: "On arrival" },
  ],
  singapore: [
    { step: "01", title: "Document Compilation",     desc: "Singapore requires a complete document set — passport, 3-month bank statement with minimum SGD 1,000 balance, employment letter, salary slips, and confirmed hotel and flight bookings.", tag: "Day 1–2" },
    { step: "02", title: "Application Preparation",  desc: "We prepare your Singapore Tourist Visa (Form 14A) and supporting sponsor letter, verifying all details against the ICA checklist for a complete submission.", tag: "Day 2" },
    { step: "03", title: "ICA Portal Submission",    desc: "Your application is submitted electronically through our authorised ICA portal access. Singapore mandates that tourist visa applications be filed through accredited agents only.", tag: "Day 2–3" },
    { step: "04", title: "ICA Processing",           desc: "The Immigration and Checkpoints Authority processes tourist visas in 3–5 working days. Additional documents may be requested during this period.", tag: "Day 3–7" },
    { step: "05", title: "Visa Issued",              desc: "Your Singapore tourist visa is issued electronically with a unique reference number. Present it at Changi Airport immigration on arrival alongside your passport.", tag: "By Day 7" },
  ],
  turkey: [
    { step: "01", title: "Application Form",          desc: "Complete the Turkey e-Visa application on the official Turkish e-Visa portal with your passport details, travel dates, and intended port of entry.", tag: "Day 1" },
    { step: "02", title: "Fee Payment and Review",    desc: "Pay the Turkey e-Visa fee online. We review the completed form before final submission — the most common error is an incorrect passport expiry date.", tag: "Day 1" },
    { step: "03", title: "Automated Processing",      desc: "Turkey's e-Visa system is largely automated. Most applications are approved instantly or within 24 hours. Complex cases may take up to 72 hours.", tag: "Instant–3 days" },
    { step: "04", title: "E-Visa Delivered",          desc: "Your Turkey e-Visa is emailed to you, allowing a 30-day stay per visit with multiple entries, valid within 180 days of issue. No physical document required at the border.", tag: "By Day 3" },
  ],
  france: [
    { step: "01", title: "Document Preparation",      desc: "Compile your passport, 2 photographs, full flight and hotel itinerary, travel insurance of at least €30,000, 6-month bank statement, and employment or business income proof.", tag: "Week 1" },
    { step: "02", title: "Application Review",         desc: "Our Schengen specialists audit every document for French Consulate compliance. France is one of the most document-intensive Schengen countries.", tag: "Week 1" },
    { step: "03", title: "VFS Appointment Booking",   desc: "We schedule your VFS Global appointment. France VFS slots are limited across Indian cities — early booking is critical, especially during peak summer season.", tag: "Week 1–2" },
    { step: "04", title: "Biometric Enrollment",       desc: "You attend the VFS centre to provide fingerprints and submit your physical passport. Mandatory for all first-time Schengen applicants.", tag: "Appointment day" },
    { step: "05", title: "Consulate Assessment",       desc: "The French Consulate reviews your application, travel itinerary, financial sufficiency, and intent to return to India. Standard decision timeline is 10–15 working days.", tag: "Week 2–3" },
    { step: "06", title: "Passport and Visa Returned", desc: "Your passport is couriered back via VFS Global with the France Schengen visa sticker, valid for your planned travel dates.", tag: "By Day 15" },
  ],
  japan: [
    { step: "01", title: "Complete Document Set",       desc: "Japan requires a full document set — passport, 3-month bank statement with minimum ₹3 lakh balance, income tax returns or Form 16, employment certificate, and a day-by-day travel itinerary.", tag: "Day 1–2" },
    { step: "02", title: "Itinerary and Cover Letter",  desc: "The Japanese Embassy closely reviews your day-wise itinerary. We prepare a detailed travel plan aligned with your hotel bookings and flight schedule.", tag: "Day 2" },
    { step: "03", title: "Embassy Submission",          desc: "Japan has no VFS centre. Applications are submitted directly to the Embassy in New Delhi or the Consulate-General in Mumbai, Bengaluru, Kolkata, or Chennai.", tag: "Day 3" },
    { step: "04", title: "Consulate Processing",        desc: "The Japanese Consulate processes applications in 5–7 working days. No biometrics are required for Indian nationals. Status cannot be tracked online.", tag: "Day 3–10" },
    { step: "05", title: "Passport Collection",         desc: "Your passport is available for in-person collection from the consulate or via authorised courier in select cities. The Japan visa is stamped directly in your passport.", tag: "By Day 10" },
  ],
  canada: [
    { step: "01", title: "Visa Category Confirmation", desc: "We confirm whether you require a Temporary Resident Visa (TRV) or qualify for an eTA. Most Indian nationals require a full TRV — not the eTA.", tag: "Day 1" },
    { step: "02", title: "Document Preparation",        desc: "Canada requires substantial documentation — 6-month bank statement, property documents, 3-year ITR, employment or business proof, and a detailed travel itinerary.", tag: "Day 1–3" },
    { step: "03", title: "IRCC Online Application",    desc: "We file your TRV application through the IRCC portal. Accuracy is critical — even minor errors can result in refusal without the possibility of appeal.", tag: "Day 3–4" },
    { step: "04", title: "Biometric Appointment",      desc: "IRCC requires biometrics (fingerprints and photo) at a Visa Application Centre. We book your appointment and provide the biometric instruction letter.", tag: "Week 1–2" },
    { step: "05", title: "Passport Submission",        desc: "After biometrics, your original passport is submitted to the VAC for the visa sticker. It remains with the centre for the full duration of processing.", tag: "After biometrics" },
    { step: "06", title: "Decision and Delivery",      desc: "IRCC processes applications in 15–25 working days. Upon approval, your passport is returned with a Canada TRV stamp valid for up to 10 years.", tag: "Week 3–5" },
  ],
  australia: [
    { step: "01", title: "Visa Subclass Selection",  desc: "We confirm you need a Subclass 600 Tourist Visa — the standard visa for Indian passport holders. The ETA (Subclass 601) is not available to Indians.", tag: "Day 1" },
    { step: "02", title: "ImmiAccount Setup",         desc: "We create or access your ImmiAccount on Australia's Department of Home Affairs portal — mandatory for lodging all Subclass 600 applications.", tag: "Day 1" },
    { step: "03", title: "Document Preparation",      desc: "Compile your passport, 6-month bank statement, property or asset evidence, employment documents, and a Genuine Temporary Entrant (GTE) statement — Australia's primary assessment criterion.", tag: "Day 1–2" },
    { step: "04", title: "Application Lodgement",     desc: "We lodge your complete application with all attachments on ImmiAccount and pay the lodgement fee. No biometrics are required for Indian nationals.", tag: "Day 2–3" },
    { step: "05", title: "Decision Issued",           desc: "Australia's Department of Home Affairs processes applications in 20–30 working days. Approval is electronic — no sticker in passport. An Electronic Visa Confirmation (ECV) is emailed.", tag: "Week 3–5" },
  ],
  "new-zealand": [
    { step: "01", title: "Visa Category Check",     desc: "Indian passport holders require a full New Zealand Visitor Visa — not the NZeTA, which is restricted to specific nationalities. We confirm the correct category for your travel.", tag: "Day 1" },
    { step: "02", title: "Document Preparation",    desc: "Prepare your passport, return flight booking, accommodation confirmation, bank statements, and evidence of strong ties to India to demonstrate genuine intent to return.", tag: "Day 1–2" },
    { step: "03", title: "INZ Online Lodgement",    desc: "We lodge your application through Immigration New Zealand's online system with all supporting documents and pay the visa fee electronically at submission.", tag: "Day 2" },
    { step: "04", title: "Processing and Decision", desc: "New Zealand processes visitor visas in 15–20 working days. Approval is electronic — the visa is linked to your passport number, not stamped physically.", tag: "Week 2–4" },
  ],
  germany: [
    { step: "01", title: "Document Preparation",      desc: "Germany requires a detailed cover letter, full flight and hotel itinerary, 6-month bank statement, income proof, NOC from employer, and travel insurance with €30,000 coverage.", tag: "Week 1" },
    { step: "02", title: "Schengen Specialist Review", desc: "Our Germany Schengen experts verify all documents against the German Consulate checklist — one of the most exacting in the Schengen area.", tag: "Week 1" },
    { step: "03", title: "VFS Appointment",            desc: "We secure your VFS Global appointment for Germany visa submission. German consulate slots fill quickly — booking 3–4 weeks in advance is strongly recommended.", tag: "Week 1–2" },
    { step: "04", title: "Biometric Collection",       desc: "You attend VFS to submit fingerprints and your physical passport. First-time Schengen applicants must provide biometrics in person without exception.", tag: "Appointment day" },
    { step: "05", title: "German Consulate Review",    desc: "The German Consulate reviews your application with particular attention to financial sufficiency — a minimum of ₹3,000 per day of stay is recommended.", tag: "Week 2–3" },
    { step: "06", title: "Visa and Passport Returned", desc: "Your passport is couriered back with the Germany Schengen visa sticker, valid for your stated travel period plus a standard buffer.", tag: "By Day 15" },
  ],
  spain: [
    { step: "01", title: "Document Assembly",          desc: "Prepare your passport, 2 photographs, complete flight and hotel bookings, 3-month bank statement, €30,000 travel insurance, and an employment or income certificate.", tag: "Week 1" },
    { step: "02", title: "Application Review",          desc: "Our Spain Schengen specialists prepare your complete file including a cover letter and financial sufficiency justification tailored to Spanish Consulate requirements.", tag: "Week 1" },
    { step: "03", title: "BLS International Appointment", desc: "Spain visas are processed through BLS International, not VFS. We book your BLS appointment — slots are limited in Delhi, Mumbai, Bengaluru, and Chennai.", tag: "Week 1–2" },
    { step: "04", title: "Biometric Submission",       desc: "Attend your BLS appointment to provide fingerprints and submit your physical passport. Some applicants may also be required to sign the application form in person.", tag: "Appointment day" },
    { step: "05", title: "Spanish Consulate Review",   desc: "The Consulate General of Spain reviews your full application, travel history, and financial documentation. Standard processing timeline is 10–15 working days.", tag: "Week 2–3" },
    { step: "06", title: "Passport Returned with Visa", desc: "BLS couriers your passport with the Spain Schengen visa sticker to your registered address. The visa is valid for your planned travel dates.", tag: "By Day 15" },
  ],
  italy: [
    { step: "01", title: "Document Compilation",               desc: "Italy requires your passport, 2 photographs sized 35mm × 45mm, full flight and hotel itinerary, 6-month bank statement, and travel insurance with minimum €30,000 medical coverage.", tag: "Week 1" },
    { step: "02", title: "Application Review",                  desc: "Our Italy Schengen specialists verify every document against Italian Consulate requirements — confirming photo specifications, insurance validity dates, and financial adequacy.", tag: "Week 1" },
    { step: "03", title: "VFS Appointment Booking",            desc: "We book your VFS Global appointment for Italy visa submission. Slots across Indian cities fill quickly — early booking prevents unnecessary delays.", tag: "Week 1–2" },
    { step: "04", title: "Biometric Enrollment and Submission", desc: "Attend VFS to provide fingerprints — mandatory for all first-time Schengen applicants — and hand in your passport for forwarding to the Italian Consulate.", tag: "Appointment day" },
    { step: "05", title: "Consulate Processing",                desc: "The Italian Consulate assesses your application and travel history. Italy processes a high volume of Schengen applications from India with a standard 15-day SLA.", tag: "Week 2–3" },
    { step: "06", title: "Passport Delivered",                  desc: "Your passport is returned via VFS Global courier with the Italy Schengen visa sticker. This single visa also grants access to all 27 Schengen member states.", tag: "By Day 15" },
  ],
};

// ─── AVATAR COLORS ────────────────────────────────────────────────────────
export const AVATAR_COLORS = ["#d97706","#059669","#0ea5e9","#7c3aed","#dc2626","#0891b2","#65a30d"];

// ─── TIER CONFIG ──────────────────────────────────────────────────────────
export const TIER_CONFIG = [
  {
    id: "client",
    name: "Client",
    desc: "Your first step into the programme",
    badge: "Tier 1",
    stripeColor: "#4a7cbf",
    badgeColor: "#4a7cbf",
    badgeBg: "#eef3fb",
    badgeBorder: "#c8d8f0",
    perks: [
      "Redeemable points on every visa application fee",
      "Access to member codes",
      "Welcome bonus OF Rs 100",
    ],
    hasBecome: true,
  },
  {
    id: "member",
    name: "Member",
    desc: "Unlocked from your 2nd application onwards",
    badge: "Tier 2",
    stripeColor: "#0d1b4b",
    badgeColor: "#0d1b4b",
    badgeBg: "#e8edf8",
    badgeBorder: "#c5cfe8",
    perks: [
      "Redeemable points on every visa application fee",
      "Access to member codes",
      "Welcome bonus OF Rs 100",
      "Dedicated agent code — every application using your code helps you earn commission",
      "Milestone rewards — achieve volume targets to unlock extra benefits",
    ],
    hasBecome: false,
  },
  {
    id: "agent",
    name: "Agent",
    desc: "For high-volume applicants & travel professionals",
    badge: "Tier 3",
    stripeColor: "#1e3a8a",
    badgeColor: "#1e3a8a",
    badgeBg: "#eef1fb",
    badgeBorder: "#c5d0f0",
    perks: [
      "Redeemable points on every visa application fee",
      "Access to member codes",
      "Welcome bonus OF Rs 100",
      "Dedicated agent code — every application using your code helps you earn commission",
      "Milestone rewards — achieve volume targets to unlock extra benefits",
      "Dedicated account support and priority service",
    ],
    hasBecome: false,
  },
];

// ─── STEP ICONS ───────────────────────────────────────────────────────────
export const STEP_ICONS = [
  createElement("svg", { key: "1", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" },
    createElement("rect", { x: "5", y: "2", width: "14", height: "20", rx: "2" }),
    createElement("path", { d: "M9 7h6M9 11h6M9 15h4" }),
    createElement("circle", { cx: "16.5", cy: "16.5", r: "3.5" }),
    createElement("path", { d: "m18.5 18.5 1.5 1.5" })
  ),
  createElement("svg", { key: "2", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" },
    createElement("circle", { cx: "11", cy: "11", r: "7" }),
    createElement("path", { d: "m21 21-4.35-4.35M8 11h6M11 8v6" })
  ),
  createElement("svg", { key: "3", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" },
    createElement("path", { d: "M3 21h18" }),
    createElement("path", { d: "M5 21V7l7-4 7 4v14" }),
    createElement("rect", { x: "9", y: "13", width: "6", height: "8" }),
    createElement("path", { d: "M9 9h.01M15 9h.01" })
  ),
  createElement("svg", { key: "4", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round" },
    createElement("rect", { x: "2", y: "7", width: "20", height: "14", rx: "2" }),
    createElement("path", { d: "M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" }),
    createElement("path", { d: "M9 14l2 2 4-4" })
  ),
];
export const rewardsData = {
  welcomeBonus: {
    amount: 100,
    description: "Instant ₹100 credited to your wallet on first sign-up",
  },
  userTiers: [
    {
      id: "candidate",
      name: "Candidate",
      icon: "🧑",
      color: "#64748b",
      description: "Your starting status when you first apply for a visa.",
      perks: [
        "₹100 welcome bonus on sign-up",
        "Access to all visa services",
        "Standard processing times",
        "Basic support via email",
      ],
      upgrade: "Apply for a 2nd visa to unlock Member status (optional)!",
    },
    {
      id: "member",
      name: "Member",
      icon: "⭐",
      color: "#d97706",
      description:
        "Upgrade after your 2nd visa application. Self-apply + apply for 1 more person.",
      perks: [
        "Apply for yourself + 1 additional person",
        "Priority customer support",
        "Exclusive member coupon codes",
        "Early access to new country launches",
        "₹150 referral bonus per successful referral",
      ],
      upgrade: "Reach 10 referrals or 5 visas to become an Agency!",
    },
    {
      id: "agency",
      name: "Agency",
      icon: "🏢",
      color: "#059669",
      description:
        "Our power users — handle bulk applications and earn maximum commissions.",
      perks: [
        "Unlimited visa applications",
        "Highest referral commissions (up to ₹500/referral)",
        "Dedicated relationship manager",
        "Agency dashboard & bulk upload",
        "Custom coupon codes for your clients",
        "Milestone bonuses & exclusive offers",
      ],
    },
  ],
  referral: {
    candidate: { amount: 100, description: "Earn ₹100 for every friend you refer who completes a visa application." },
    member:    { amount: 150, description: "Earn ₹150 per referral + bonus on their service fee." },
    agency:    { amount: 500, description: "Earn up to ₹500 per referred application across all clients." },
  },
  coupons: {
    member: {
      example: "MEMBER20",
      discount: "20% off service fee",
      description: "Members get an exclusive coupon code to share. When a friend uses your code, both of you save!",
    },
    agency: {
      example: "AGENCY30",
      discount: "30% off service fee",
      description: "Agencies get custom high-value coupon codes for their client base.",
    },
  },
  milestones: [
    { target: 5,   reward: "₹500 bonus + Member badge upgrade", icon: "🎯" },
    { target: 10,  reward: "₹1,000 bonus + priority slot access", icon: "✈️" },
    { target: 25,  reward: "₹3,000 bonus + Agency status unlock", icon: "🏆" },
    { target: 50,  reward: "₹7,500 bonus + dedicated account manager", icon: "💎" },
    { target: 100, reward: "₹20,000 bonus + co-branding opportunity", icon: "👑" },
  ],
  transitVisa: {
    description:
      "Candidate status includes +1 transit visa benefit — apply for a transit visa alongside your main visa at no extra membership cost.",
  },
};

// ─── VISA LIST ──────────────────────────────────────────────────────────────
export const visaList = [
  { id: "uae",         country: "United Arab Emirates", flag: "🇦🇪" },
  { id: "thailand",    country: "Thailand",              flag: "🇹🇭" },
  { id: "switzerland", country: "Switzerland",           flag: "🇨🇭" },
  { id: "vietnam",     country: "Vietnam",               flag: "🇻🇳" },
  { id: "indonesia",   country: "Indonesia",             flag: "🇮🇩" },
  { id: "maldives",    country: "Maldives",              flag: "🇲🇻" },
  { id: "usa",         country: "United States",         flag: "🇺🇸" },
  { id: "hong-kong",   country: "Hong Kong",             flag: "🇭🇰" },
  { id: "egypt",       country: "Egypt",                 flag: "🇪🇬" },
  { id: "malaysia",    country: "Malaysia",              flag: "🇲🇾" },
  { id: "singapore",   country: "Singapore",             flag: "🇸🇬" },
  { id: "turkey",      country: "Turkey",                flag: "🇹🇷" },
  { id: "france",      country: "France",                flag: "🇫🇷" },
  { id: "japan",       country: "Japan",                 flag: "🇯🇵" },
  { id: "canada",      country: "Canada",                flag: "🇨🇦" },
  { id: "australia",   country: "Australia",             flag: "🇦🇺" },
  { id: "new-zealand", country: "New Zealand",           flag: "🇳🇿" },
  { id: "germany",     country: "Germany",               flag: "🇩🇪" },
  { id: "spain",       country: "Spain",                 flag: "🇪🇸" },
  { id: "italy",       country: "Italy",                 flag: "🇮🇹" },
];

// ─── COUNTRY DATA ───────────────────────────────────────────────────────────
const DATA = {

  // ── UAE ────────────────────────────────────────────────────────────────────
  uae: {
    heroImg: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1600&q=85",
    heroAlt: "Dubai skyline at dusk",
    country: "United Arab Emirates",
    flag: "🇦🇪",
    tagline: "One Visa. Seven Emirates. Infinite Memories.",
    breadcrumb: ["Home", "United Arab Emirates"],
    accentColor: "#d97706",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "53K+",   label: "Visas Processed" },
      { val: "4 Days", label: "Duration" },
      { val: "4.9★",   label: "12K+ Reviews" },
      { val: "₹6,800", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "E-Visa (Paperless)",
      stay: "30 days",
      validity: "60 days",
      entry: "Single",
      govFee: 6800,
      svcFee: 999,
      svcOrig: 1500,
      guarantee: "Approval in 4 days",
      expressFee: 2000,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa", desc: "Submit your documents online. Pay only the government fee upfront — nothing else." },
      { icon: "🔍", step: "02", title: "Expert Verification", desc: "Our visa specialists personally review every document before submission to immigration." },
      { icon: "⚙️", step: "03", title: "Official Processing", desc: "We coordinate directly with UAE Immigration for timely, accurate processing." },
      { icon: "✅", step: "04", title: "Visa Delivered", desc: "Receive your approved e-Visa via email — ready to fly!" },
    ],
   
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your travel date",              img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent photo, white background, taken within 6 months",         img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight booking",                                img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for your entire stay",                   img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",   desc: "Last 3 months showing sufficient funds",                         img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance", desc: "Adequate coverage for the duration of your trip",               img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Dubai",          img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=600&q=80" },
      { name: "Abu Dhabi",      img: "https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&q=80" },
      { name: "Sharjah",        img: "https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?w=600&q=80" },
      { name: "Ras Al Khaimah", img: "https://images.unsplash.com/photo-1580674684081-7617fbf3d745?w=600&q=80" },
      { name: "Fujairah",       img: "https://images.unsplash.com/photo-1590559899731-a382839e5549?w=600&q=80" },
      { name: "Umm Al Quwain",  img: "https://images.unsplash.com/photo-1548700960-927acf3b1dc0?w=600&q=80" },
    ],
    faqs: [
      { q: "How long does a UAE visa take?",          a: "With TheVisa, your UAE tourist visa is guaranteed in 4 days. Urgent same-day processing is also available." },
      { q: "What is the total cost for Indians?",     a: "Government fee: ₹6,800 (upfront). Service fee: ₹999 (after approval). Total: ₹7,799. Zero hidden charges." },
      { q: "Can I visit all 7 Emirates with one visa?", a: "Yes! A single UAE visa grants full access to all 7 Emirates — Dubai, Abu Dhabi, Sharjah, Ras Al Khaimah, Fujairah, Umm Al Quwain, and Ajman." },
      { q: "Is UAE visa on arrival available for Indians?", a: "No. Indians must apply for a UAE e-Visa before travelling. TheVisa handles this 100% online." },
      { q: "What documents are required?",            a: "Valid passport (6+ months), recent passport photo, confirmed return tickets, hotel booking, and 3 months bank statement." },
      { q: "What is TheVisa Protect?",                a: "TheVisa Protect is included FREE with every application. If your visa is delayed — you pay zero service fees. If rejected — 100% refund guaranteed." },
    ],
    reviews: [
      { name: "Ananya Sharma", rating: 5, date: "Feb 2026", title: "Super fast & stress-free!",   body: "Got my UAE visa in just 3 days. Completely paperless and the team was incredibly helpful!" },
      { name: "Rohit Verma",   rating: 5, date: "Jan 2026", title: "100% recommended",            body: "Received my visa within the guaranteed timeframe. All fees shown upfront — zero hidden charges." },
      { name: "Priya Nair",    rating: 5, date: "Mar 2026", title: "Professional & transparent", body: "Loved how transparent the pricing was. Got my visa exactly when promised." },
      { name: "Karan Mehta",   rating: 4, date: "Feb 2026", title: "Great experience overall",   body: "Seamless application process. Support team responded instantly on WhatsApp." },
      { name: "Karan Mehta",   rating: 4, date: "Feb 2026", title: "Great experience overall",   body: "Seamless application process. Support team responded instantly on WhatsApp." },
    ],
    reviewScore: "4.9",
    reviewCount: "12,400+",
    authBadge: "Authorized by the Government of Dubai",
  },

  // ── THAILAND ───────────────────────────────────────────────────────────────
  thailand: {
    heroImg: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1600&q=85",
    heroAlt: "Bangkok Thailand temples",
    country: "Thailand",
    flag: "🇹🇭",
    tagline: "Land of Smiles Awaits.",
    breadcrumb: ["Home", "Thailand"],
    accentColor: "#c2410c",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "28K+",   label: "Visas Processed" },
      { val: "5 Days", label: "Duration" },
      { val: "4.8★",   label: "8K+ Reviews" },
      { val: "₹3,200", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "TR Visa (E-Visa)",
      stay: "30 days",
      validity: "90 days",
      entry: "Single",
      govFee: 3200,
      svcFee: 799,
      svcOrig: 1299,
      guarantee: "Approval in 5 days",
      expressFee: 1500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",      desc: "Submit your documents online. Pay only the Thai government fee upfront." },
      { icon: "🔍", step: "02", title: "Expert Verification",   desc: "Our specialists review and translate documents if required before Thai Immigration." },
      { icon: "⚙️", step: "03", title: "Official Processing",   desc: "We coordinate with Thai Immigration for timely, accurate processing." },
      { icon: "✅", step: "04", title: "e-Visa Delivered",      desc: "Receive your Thailand e-Visa via email — print and carry for border entry." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your travel date",               img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent photo on white background, taken within 6 months",         img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight booking",                                  img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for your entire stay",                     img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",   desc: "Last 3 months — minimum ₹50,000 balance",                         img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance", desc: "Minimum coverage of USD 50,000 recommended",                      img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Bangkok",    img: "https://images.unsplash.com/photo-1563492065599-3520f775eeed?w=600&q=80" },
      { name: "Phuket",     img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Chiang Mai", img: "https://images.unsplash.com/photo-1583417319070-4a69db38a482?w=600&q=80" },
      { name: "Pattaya",    img: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?w=600&q=80" },
      { name: "Koh Samui",  img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80" },
      { name: "Krabi",      img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Thailand?",              a: "Indians no longer get visa-on-arrival free as of 2025. Apply online via TheVisa — we process your Thai TR visa within 5 guaranteed days." },
      { q: "What is the cost of a Thailand visa for Indians?",  a: "Government fee: ₹3,200 (upfront). TheVisa service fee: ₹799 (after approval). Total: ₹3,999." },
      { q: "How long can I stay in Thailand?",                  a: "A standard Thai tourist visa allows a 30-day stay, which can be extended once for another 30 days at an immigration office in Thailand." },
      { q: "Can I apply for a multiple-entry Thailand visa?",   a: "Yes, a Multiple Entry Tourist Visa (METV) is available, valid for 6 months with 60 days per entry. Ask our team for details." },
      { q: "Is Thailand safe for Indian travellers?",           a: "Thailand is one of the safest destinations for Indian tourists. The country has a large Indian community and Hindi-speaking guides are widely available." },
      { q: "Do I need a Thai bank account to get a visa?",      a: "No. You just need to show an Indian bank statement with a minimum balance of ₹50,000 for a 30-day trip." },
    ],
    reviews: [
      { name: "Pooja Menon",  rating: 5, date: "Feb 2026", title: "Thailand made easy!", body: "Got my Thailand visa in 4 days. The team was super responsive. 10/10!" },
      { name: "Rahul Kapoor", rating: 5, date: "Jan 2026", title: "Stress-free holiday prep", body: "TheVisa made the Thailand visa process completely hassle-free. Approved early!" },
      { name: "Divya Reddy",  rating: 4, date: "Mar 2026", title: "Great service, minor delay", body: "Got visa in 6 days (1 past guarantee) but they waived the service fee as promised." },
      { name: "Vikram Nair",  rating: 5, date: "Feb 2026", title: "Will use again!", body: "Third time using TheVisa — this time for Thailand. Consistent quality every time." },
    ],
    reviewScore: "4.8",
    reviewCount: "8,000+",
    authBadge: "Authorized by the Royal Thai Embassy",
  },

  // ── SWITZERLAND ────────────────────────────────────────────────────────────
  switzerland: {
    heroImg: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=1600&q=85",
    heroAlt: "Swiss Alps and lake",
    country: "Switzerland",
    flag: "🇨🇭",
    tagline: "Alps, Chocolates & Precision.",
    breadcrumb: ["Home", "Switzerland"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "30K+",    label: "Visas Processed" },
      { val: "15 Days", label: "Duration" },
      { val: "4.9★",    label: "7K+ Reviews" },
      { val: "₹7,200",  label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Schengen Visa (Type C)",
      stay: "90 days",
      validity: "180 days",
      entry: "Multiple",
      govFee: 7200,
      svcFee: 1499,
      svcOrig: 2299,
      guarantee: "Approval in 15 days",
      expressFee: 3500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",        desc: "Fill the form and upload documents — pay only the Schengen government fee upfront." },
      { icon: "🔍", step: "02", title: "Schengen Expert Review",  desc: "Swiss consulate specialists verify every document for compliance." },
      { icon: "📅", step: "03", title: "VFS Appointment",         desc: "We book your VFS Global biometric appointment and submit your full file." },
      { icon: "✅", step: "04", title: "Passport Delivered",      desc: "Your passport with Schengen sticker is couriered to your doorstep." },
    ],
    
    essentials: [
      { icon: "🛂", title: "Passport",          desc: "Valid for at least 3 months beyond your intended return date",    img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",    desc: "Recent photo on white background, 35mm x 45mm",                  img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",     desc: "Confirmed return flight booking",                                 img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",     desc: "Confirmed accommodation for entire stay",                         img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",    desc: "Last 6 months — minimum ₹3,00,000 recommended",                  img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance",  desc: "Minimum €30,000 coverage — mandatory for all Schengen visas",    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Zurich",         img: "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=600&q=80" },
      { name: "Geneva",         img: "https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=600&q=80" },
      { name: "Interlaken",     img: "https://images.unsplash.com/photo-1527668752968-14dc70a27c95?w=600&q=80" },
      { name: "Lucerne",        img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Jungfraujoch",   img: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=600&q=80" },
      { name: "Bern",           img: "https://images.unsplash.com/photo-1529927120475-c2a81aa28f6b?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Switzerland?",          a: "Yes. Switzerland is part of the Schengen Area — Indians must apply for a Schengen visa to enter." },
      { q: "What is the Switzerland visa fee for Indians?",    a: "Government fee: ₹7,200 (upfront). TheVisa service fee: ₹1,499 (after approval). Total: ₹8,699." },
      { q: "Can I travel to other Schengen countries on this visa?", a: "Yes. A Swiss Schengen visa lets you visit all 27 Schengen countries, but Switzerland should be your primary destination." },
      { q: "How much bank balance is required for Switzerland?", a: "The Swiss consulate recommends at least CHF 100 per day (approx ₹9,500/day). A solid 6-month bank statement is essential." },
      { q: "Is travel insurance mandatory for Switzerland?",   a: "Yes. Schengen rules require travel insurance with at least €30,000 medical coverage. TheVisa can recommend trusted providers." },
      { q: "How long can I stay in Switzerland?",              a: "Up to 90 days within any 180-day period across the entire Schengen Area." },
    ],
    reviews: [
      { name: "Shreya Malhotra", rating: 5, date: "Feb 2026", title: "Switzerland was magical!", body: "TheVisa handled my Swiss Schengen visa perfectly. Got it in 13 days, just in time for my trip!" },
      { name: "Arjun Bose",      rating: 5, date: "Jan 2026", title: "Flawless process",         body: "Document checklist was very detailed. No extra visits, no confusion. Visa in hand — sorted!" },
      { name: "Nisha Patel",     rating: 4, date: "Mar 2026", title: "Good service, worth it",   body: "A bit pricey but the peace of mind is 100% worth it. Switzerland was breathtaking." },
      { name: "Dev Agarwal",     rating: 5, date: "Feb 2026", title: "Seamless Schengen!",       body: "First Schengen visa and TheVisa made it incredibly easy. No VFS confusion whatsoever." },
    ],
    reviewScore: "4.9",
    reviewCount: "7,000+",
    authBadge: "Authorized Schengen visa facilitation partner",
  },

  // ── VIETNAM ────────────────────────────────────────────────────────────────
  vietnam: {
    heroImg: "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=1600&q=85",
    heroAlt: "Ha Long Bay Vietnam",
    country: "Vietnam",
    flag: "🇻🇳",
    tagline: "Discover Vietnam.",
    breadcrumb: ["Home", "Vietnam"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "27K+",   label: "Visas Processed" },
      { val: "3 Days", label: "Duration" },
      { val: "4.8★",   label: "6K+ Reviews" },
      { val: "₹2,500", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "E-Visa (Single/Multiple)",
      stay: "90 days",
      validity: "90 days",
      entry: "Single or Multiple",
      govFee: 2500,
      svcFee: 699,
      svcOrig: 999,
      guarantee: "Approval in 3 days",
      expressFee: 1200,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",      desc: "Submit your passport and photo online. Pay the Vietnam government fee upfront." },
      { icon: "🔍", step: "02", title: "Document Check",        desc: "Our Vietnam specialists verify all details to ensure zero issues at entry." },
      { icon: "⚙️", step: "03", title: "E-Visa Processing",    desc: "We submit directly to Vietnam's e-Visa portal and track in real time." },
      { icon: "✅", step: "04", title: "E-Visa Delivered",     desc: "Your Vietnam e-Visa PDF arrives by email — print and present on arrival." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your travel date",               img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent colour photo on white background",                         img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight booking",                                  img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Accommodation",    desc: "Hotel booking or host confirmation for your stay",                 img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
    ],
    destinations: [
      { name: "Hanoi",      img: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=600&q=80" },
      { name: "Ho Chi Minh City", img: "https://images.unsplash.com/photo-1528360983277-13d401cdc186?w=600&q=80" },
      { name: "Ha Long Bay", img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Hội An",     img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Da Nang",    img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Phú Quốc",   img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Vietnam?",               a: "Yes. Indian passport holders require a visa for Vietnam. TheVisa provides the e-Visa within 3 working days." },
      { q: "How much does a Vietnam visa cost for Indians?",    a: "Government fee: ₹2,500 (upfront). TheVisa service fee: ₹699 (after approval). Total: ₹3,199." },
      { q: "How long can I stay in Vietnam on an e-Visa?",      a: "The Vietnam e-Visa allows up to 90 days stay — one of the most generous in Asia." },
      { q: "Can I enter Vietnam multiple times on one visa?",   a: "Yes! You can apply for a Multiple Entry e-Visa valid for 90 days, allowing multiple entries within the validity period." },
      { q: "Which airports support Vietnam e-Visa entry?",      a: "All international airports including Hanoi (Noi Bai), Ho Chi Minh City (Tan Son Nhat), and Da Nang accept e-Visa entry." },
      { q: "Can I extend my Vietnam e-Visa?",                   a: "Extensions are possible in-country at immigration offices. TheVisa can guide you through the extension process." },
    ],
    reviews: [
      { name: "Swati Joshi",    rating: 5, date: "Feb 2026", title: "Vietnam in 2 days!",         body: "Got my Vietnam e-Visa in just 2 days — one day before the guarantee. Phenomenal!" },
      { name: "Prakash Rao",    rating: 5, date: "Jan 2026", title: "Best value visa service",   body: "Cheapest and fastest visa I've ever applied for. Vietnam is now my annual getaway!" },
      { name: "Neha Srivastava", rating: 4, date: "Mar 2026", title: "Quick and reliable",       body: "Visa came in exactly 3 days. Simple document requirements. All very straightforward." },
      { name: "Aditya Shah",    rating: 5, date: "Feb 2026", title: "Highly recommended!",       body: "Applied for family of 3. All three Vietnam visas processed together without any issues." },
    ],
    reviewScore: "4.8",
    reviewCount: "6,000+",
    authBadge: "Authorized Vietnam e-Visa facilitation partner",
  },

  // ── INDONESIA ──────────────────────────────────────────────────────────────
  indonesia: {
    heroImg: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1600&q=85",
    heroAlt: "Bali Indonesia rice terraces",
    country: "Indonesia",
    flag: "🇮🇩",
    tagline: "Bali & Beyond — Visa on Arrival Made Easy.",
    breadcrumb: ["Home", "Indonesia"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "16K+",   label: "Visas Processed" },
      { val: "2 Days", label: "Duration" },
      { val: "4.8★",   label: "4K+ Reviews" },
      { val: "₹2,200", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "VOA / E-Visa on Arrival",
      stay: "30 days",
      validity: "30 days",
      entry: "Single",
      govFee: 2200,
      svcFee: 599,
      svcOrig: 899,
      guarantee: "Approval in 2 days",
      expressFee: 1000,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",      desc: "Fill the form and upload your passport copy. Pay the Indonesia govt fee upfront." },
      { icon: "🔍", step: "02", title: "Document Review",       desc: "Our Indonesia specialists verify all details for a smooth arrival experience." },
      { icon: "⚙️", step: "03", title: "E-VOA Processing",     desc: "We process your B211A e-VOA through official Indonesia immigration channels." },
      { icon: "✅", step: "04", title: "Approval Delivered",    desc: "Receive your e-VOA via email — show QR code on arrival at Bali or other airports." },
    ],
 
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your arrival date in Indonesia",  img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed onward or return flight from Indonesia",                  img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "At least first night accommodation confirmation",                   img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Sufficient Funds", desc: "Bank card or cash equivalent to USD 500 per person",               img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
    ],
    destinations: [
      { name: "Bali",         img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80" },
      { name: "Jakarta",      img: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=600&q=80" },
      { name: "Lombok",       img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "Komodo Island", img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Yogyakarta",   img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Raja Ampat",   img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians get visa on arrival for Bali?",          a: "Yes, Indians are eligible for Visa on Arrival (VOA) or the e-VOA in Indonesia. TheVisa pre-processes your e-VOA so you skip airport queues." },
      { q: "How much does Indonesia visa cost for Indians?",    a: "Government fee: ₹2,200 (upfront). TheVisa service fee: ₹599 (after approval). Total: ₹2,799." },
      { q: "How long can I stay in Indonesia?",                 a: "Standard VOA allows 30 days. You can extend once for another 30 days at an Indonesian immigration office." },
      { q: "What is the difference between VOA and e-VOA?",     a: "VOA is paid on arrival at the airport. e-VOA (which TheVisa processes) is paid and approved before travel — faster and queue-free." },
      { q: "Is the Indonesia visa single or multiple entry?",   a: "The standard e-VOA is single entry. Multiple entry options are available for longer stays — ask our team." },
      { q: "Can I visit other Indonesian islands on one visa?", a: "Yes! A single Indonesia visa grants access to all islands — Bali, Lombok, Java, Komodo, and more." },
    ],
    reviews: [
      { name: "Tanya Mehta",  rating: 5, date: "Feb 2026", title: "Bali trip sorted!",         body: "Pre-approved e-VOA meant I walked straight through immigration at Denpasar. Loved the service!" },
      { name: "Siddharth Kumar", rating: 5, date: "Jan 2026", title: "Super quick processing", body: "Got my Indonesia e-VOA in less than 24 hours. Absolutely zero stress at the airport." },
      { name: "Priyanka Das", rating: 4, date: "Mar 2026", title: "Smooth Bali experience",    body: "e-VOA process was clean and fast. Minor UI issue on upload but support fixed it in minutes." },
      { name: "Mohit Sharma", rating: 5, date: "Feb 2026", title: "Best Bali visa service",    body: "Third time applying through TheVisa for Bali. Still the fastest and most reliable option." },
    ],
    reviewScore: "4.8",
    reviewCount: "4,000+",
    authBadge: "Authorized Indonesia e-VOA facilitation partner",
  },

  // ── MALDIVES ───────────────────────────────────────────────────────────────
  maldives: {
    heroImg: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=1600&q=85",
    heroAlt: "Maldives overwater bungalows",
    country: "Maldives",
    flag: "🇲🇻",
    tagline: "Paradise on Earth — Free Visa on Arrival for Indians!",
    breadcrumb: ["Home", "Maldives"],
    accentColor: "#0891b2",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "17K+",   label: "Trips Arranged" },
      { val: "FREE",   label: "Visa on Arrival" },
      { val: "4.9★",   label: "5K+ Reviews" },
      { val: "₹0",     label: "Visa Cost" },
    ],
    visaCard: {
      type: "Visa on Arrival (Free)",
      stay: "30 days",
      validity: "30 days",
      entry: "Single",
      govFee: 0,
      svcFee: 499,
      svcOrig: 799,
      guarantee: "Pre-arrival documentation",
      expressFee: 800,
      badge: "100% Assistance Approval",
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",      desc: "Submit your travel details — we prepare all your Maldives pre-arrival documentation." },
      { icon: "🔍", step: "02", title: "Document Preparation",  desc: "We verify your hotel booking, return ticket, and ensure compliance with Maldivian rules." },
      { icon: "✈️", step: "03", title: "Pre-Arrival Clearance", desc: "Your documents are prepared for the seamless Maldives immigration process on arrival." },
      { icon: "✅", step: "04", title: "Arrive & Enjoy",        desc: "Receive your free 30-day visa stamp at Malé International Airport on arrival." },
    ],
 
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your travel date",               img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight from Maldives is mandatory for entry",     img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed resort or guesthouse booking for your entire stay",      img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Sufficient Funds", desc: "USD 100/day recommended per person or a valid credit card",        img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
    ],
    destinations: [
      { name: "Malé Atoll",      img: "https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=600&q=80" },
      { name: "Baa Atoll",       img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Ari Atoll",       img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "North Malé",      img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80" },
      { name: "Raa Atoll",       img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Lhaviyani Atoll", img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Maldives?",              a: "No! Indians get a free 30-day Visa on Arrival at Malé International Airport. No pre-visa required." },
      { q: "What is the cost of visiting Maldives for Indians?", a: "Visa is completely free. TheVisa charges only ₹499 for pre-arrival documentation and coordination services." },
      { q: "What documents are required for Maldives entry?",   a: "Valid passport, return ticket, confirmed hotel/resort booking, and proof of sufficient funds (USD 100/day recommended)." },
      { q: "How long can I stay in Maldives?",                  a: "The free Visa on Arrival grants 30 days. Extensions can be applied for within Maldives for up to 90 days total." },
      { q: "Is Maldives expensive for Indian tourists?",        a: "Maldives has options for every budget — from guesthouse islands to luxury over-water villas. Budget trips are very much possible." },
      { q: "What is the best time to visit Maldives from India?", a: "November to April is peak season with calm seas and clear skies. May to October is off-season with lower resort prices." },
    ],
    reviews: [
      { name: "Ritu Sharma",    rating: 5, date: "Feb 2026", title: "Paradise achieved!",         body: "TheVisa's Maldives coordination was flawless. Resort booking, speedboat — all taken care of!" },
      { name: "Kunal Saxena",   rating: 5, date: "Jan 2026", title: "Hassle-free honeymoon",     body: "Our Maldives honeymoon was perfect partly because TheVisa handled all the paperwork." },
      { name: "Ishita Verma",   rating: 5, date: "Mar 2026", title: "Arrived stress-free!",      body: "Loved that everything was pre-arranged. Walked through Malé airport in under 10 minutes." },
      { name: "Abhijit Nair",   rating: 4, date: "Feb 2026", title: "Great value service",       body: "Free visa + ₹499 prep fee = completely worth it. Maldives is everything they say it is." },
    ],
    reviewScore: "4.9",
    reviewCount: "5,000+",
    authBadge: "Authorized Maldives travel documentation partner",
  },

  // ── USA ────────────────────────────────────────────────────────────────────
  usa: {
    heroImg: "https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=1600&q=85",
    heroAlt: "New York City skyline",
    country: "United States",
    flag: "🇺🇸",
    tagline: "The American Dream — Expert B1/B2 Visa Assistance.",
    breadcrumb: ["Home", "United States"],
    accentColor: "#1d4ed8",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "12K+",    label: "Visas Processed" },
      { val: "99%",     label: "Doc Accuracy" },
      { val: "4.8★",    label: "3K+ Reviews" },
      { val: "₹14,500", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "B1/B2 Tourist & Business Visa",
      stay: "Up to 180 days",
      validity: "10 years",
      entry: "Multiple",
      govFee: 14500,
      svcFee: 1999,
      svcOrig: 2999,
      guarantee: "Application in 24 hours",
      expressFee: 5000,
    },
    process: [
      { icon: "📝", step: "01", title: "DS-160 & Application",     desc: "We help complete and review your DS-160 form with 100% accuracy before submission." },
      { icon: "🔍", step: "02", title: "Document Checklist",        desc: "Our US visa experts prepare a personalised checklist and review all documents." },
      { icon: "🎤", step: "03", title: "Interview Preparation",     desc: "Mock interview sessions and likely questions tailored to your profile." },
      { icon: "✅", step: "04", title: "Visa Collection",           desc: "Passport with US visa delivered to your doorstep after embassy approval." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Valid Passport",         desc: "Must be valid for at least 6 months beyond your stay period",     img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "📋", title: "DS-160 Form",            desc: "Completed online application form — we help you fill it perfectly", img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",         desc: "5x5 cm, white background, taken within 6 months",                  img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",         desc: "Last 6 months — strong balance significantly improves approval",   img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "👔", title: "Employment / ITR Docs",  desc: "Employer letter, salary slips, or ITR for last 3 years",          img: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=400&q=80" },
      { icon: "✈️", title: "Travel Itinerary",       desc: "Detailed travel plan including flights and accommodation",          img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
    ],
    destinations: [
      { name: "New York",      img: "https://images.unsplash.com/photo-1485738422979-f5c462d49f74?w=600&q=80" },
      { name: "Los Angeles",   img: "https://images.unsplash.com/photo-1534190760961-74e8c1c5c3da?w=600&q=80" },
      { name: "Las Vegas",     img: "https://images.unsplash.com/photo-1605833556294-ea5c7a74f57d?w=600&q=80" },
      { name: "San Francisco", img: "https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=600&q=80" },
      { name: "Miami",         img: "https://images.unsplash.com/photo-1605723517503-3cadb5818a0c?w=600&q=80" },
      { name: "Chicago",       img: "https://images.unsplash.com/photo-1494522855154-9297ac14b55f?w=600&q=80" },
    ],
    faqs: [
      { q: "How long does a US visa take for Indians?",           a: "Processing typically takes 2-6 weeks after the interview. TheVisa helps secure the earliest available interview slot." },
      { q: "What is the US visa fee for Indians?",                a: "Government (MRV) fee: ₹14,500 (non-refundable). TheVisa service fee: ₹1,999 (charged only after approval)." },
      { q: "Is the US visa valid for 10 years?",                  a: "If approved, most Indians receive a 10-year multiple-entry B1/B2 visa. Duration is at the consular officer's discretion." },
      { q: "Can TheVisa guarantee US visa approval?",             a: "No one can guarantee US approval — it's at the consular officer's discretion. TheVisa maximises your chances with perfect documentation and interview prep." },
      { q: "How early should I apply for a US visa?",             a: "Apply at least 3-4 months before your planned travel date, as US embassy interview slots are often scarce." },
      { q: "What are common reasons for US visa rejection?",      a: "Strong ties to India, insufficient funds, incomplete DS-160, or unclear travel purpose are common reasons. TheVisa's expert review addresses all these." },
    ],
    reviews: [
      { name: "Harish Iyer",   rating: 5, date: "Feb 2026", title: "Got my 10-year US visa!",  body: "TheVisa's document checklist was incredibly thorough. Interview prep sessions were super helpful. Approved first attempt!" },
      { name: "Nalini Sharma", rating: 5, date: "Jan 2026", title: "Brilliant support team",   body: "The team was with me every step. DS-160 filled correctly, documents perfect — visa approved without issues." },
      { name: "Ramesh Pillai", rating: 4, date: "Mar 2026", title: "Very helpful interview prep", body: "Mock interview was exactly like the real thing. Only 4 questions at the embassy!" },
      { name: "Asha Murthy",   rating: 5, date: "Feb 2026", title: "Family US visa sorted!",   body: "Applied for 4 family members. All four got their US visas with TheVisa's help. Stellar service!" },
    ],
    reviewScore: "4.8",
    reviewCount: "3,000+",
    authBadge: "Authorized US visa application facilitator",
  },

  // ── HONG KONG ──────────────────────────────────────────────────────────────
  "hong-kong": {
    heroImg: "https://images.unsplash.com/photo-1536599018102-9f803c140fc1?w=1600&q=85",
    heroAlt: "Hong Kong harbour skyline at night",
    country: "Hong Kong",
    flag: "🇭🇰",
    tagline: "The Pearl of the Orient. ",
    breadcrumb: ["Home", "Hong Kong"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "19K+",   label: "Visas Processed" },
      { val: "5 Days", label: "Duration" },
      { val: "4.8★",   label: "4K+ Reviews" },
      { val: "₹3,600", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "HKSAR Visitor Visa",
      stay: "14 days",
      validity: "3 months",
      entry: "Single",
      govFee: 3600,
      svcFee: 899,
      svcOrig: 1299,
      guarantee: "Approval in 5 days",
      expressFee: 1800,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",       desc: "Submit your documents online. Pay only the Hong Kong immigration fee upfront." },
      { icon: "🔍", step: "02", title: "HKSAR Expert Review",    desc: "Our specialists verify all documents for Hong Kong Immigration Department compliance." },
      { icon: "⚙️", step: "03", title: "Immigration Processing", desc: "We coordinate with HKSAR Immigration for fast and accurate approval." },
      { icon: "✅", step: "04", title: "Visa Delivered",         desc: "Receive your Hong Kong visitor visa via email — ready for travel." },
    ],
 
    essentials: [
      { icon: "🛂", title: "Passport",          desc: "Valid for at least 6 months from travel date",                    img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",    desc: "Recent colour photo on white background",                         img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",     desc: "Confirmed return or onward travel from Hong Kong",                img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",     desc: "Confirmed accommodation for entire stay",                         img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",    desc: "Last 3 months showing sufficient funds",                          img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
    ],
    destinations: [
      { name: "Victoria Harbour", img: "https://images.unsplash.com/photo-1536599018102-9f803c140fc1?w=600&q=80" },
      { name: "Lantau Island",    img: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=600&q=80" },
      { name: "Mong Kok",         img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Repulse Bay",      img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "The Peak",         img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Macau (Day Trip)", img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Hong Kong?",             a: "Yes. Indian passport holders require a visitor visa for Hong Kong. TheVisa processes it within 5 guaranteed days." },
      { q: "What is the Hong Kong visa fee for Indians?",       a: "Government fee: ₹3,600 (upfront). TheVisa service fee: ₹899 (after approval). Total: ₹4,499." },
      { q: "How long can I stay in Hong Kong?",                 a: "A standard visitor visa allows 14 days. Extensions can be requested at HKSAR immigration." },
      { q: "Can I visit Macau on a Hong Kong visa?",            a: "Yes! Macau is a separate territory and requires its own entry but Indian passport holders can visit Macau without a visa for up to 30 days." },
      { q: "Is Hong Kong part of China?",                       a: "Hong Kong is a Special Administrative Region (SAR) of China. It operates under a separate immigration system from mainland China." },
      { q: "Can I extend my Hong Kong visa?",                   a: "Extensions can be applied for at the Immigration Department in Hong Kong before your visa expires." },
    ],
    reviews: [
      { name: "Sameer Jain",    rating: 5, date: "Feb 2026", title: "HK was incredible!",       body: "Visa came in 4 days. TheVisa's process was smooth and the team was very responsive." },
      { name: "Kavitha Reddy",  rating: 5, date: "Jan 2026", title: "Fast and professional",   body: "Applied for Hong Kong visa and got approved in under 5 days. Highly recommend!" },
      { name: "Ajay Pandey",    rating: 4, date: "Mar 2026", title: "Good service",             body: "Minor document query was resolved quickly by support. Overall excellent experience." },
      { name: "Sunaina Bhat",   rating: 5, date: "Feb 2026", title: "Loved the transparency",  body: "Upfront pricing, no hidden fees. Got exactly what was promised. 5 stars!" },
    ],
    reviewScore: "4.8",
    reviewCount: "4,000+",
    authBadge: "Authorized HKSAR visa application facilitator",
  },

  // ── EGYPT ──────────────────────────────────────────────────────────────────
  egypt: {
    heroImg: "https://images.unsplash.com/photo-1568322445389-f64ac2515020?w=1600&q=85",
    heroAlt: "Pyramids of Giza Egypt",
    country: "Egypt",
    flag: "🇪🇬",
    tagline: "Land of the Pharaohs.",
    breadcrumb: ["Home", "Egypt"],
    accentColor: "#d97706",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "24K+",   label: "Visas Processed" },
      { val: "5 Days", label: "Duration" },
      { val: "4.8★",   label: "5K+ Reviews" },
      { val: "₹4,000", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "E-Visa (Tourist)",
      stay: "30 days",
      validity: "3 months",
      entry: "Single",
      govFee: 4000,
      svcFee: 999,
      svcOrig: 1499,
      guarantee: "Approval in 5 days",
      expressFee: 1500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",       desc: "Upload your passport and photo online. Pay the Egypt government fee upfront." },
      { icon: "🔍", step: "02", title: "Expert Document Review", desc: "Our Egypt visa experts verify all details before submission to the Ministry of Interior." },
      { icon: "⚙️", step: "03", title: "E-Visa Processing",     desc: "We track your application through Egypt's official e-Visa portal." },
      { icon: "✅", step: "04", title: "E-Visa Delivered",       desc: "Receive your Egypt e-Visa PDF via email — print and present at Cairo airport." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your travel date",               img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent colour photo on white background",                         img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight from Egypt",                               img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for your stay",                            img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",   desc: "Last 3 months showing sufficient travel funds",                   img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
    ],
    destinations: [
      { name: "Cairo & Pyramids", img: "https://images.unsplash.com/photo-1568322445389-f64ac2515020?w=600&q=80" },
      { name: "Luxor",            img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Aswan",            img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Sharm El Sheikh",  img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "Hurghada",         img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Alexandria",       img: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Egypt?",               a: "Yes. Indian passport holders need a visa for Egypt. TheVisa provides the e-Visa online within 5 guaranteed days." },
      { q: "What is the Egypt visa fee for Indians?",         a: "Government fee: ₹4,000 (upfront). TheVisa service fee: ₹999 (after approval). Total: ₹4,999." },
      { q: "How long can I stay in Egypt on a tourist visa?", a: "The standard Egypt e-Visa allows 30 days per entry and is valid for 3 months from issue date." },
      { q: "Can I visit the Pyramids on an e-Visa?",          a: "Absolutely! Your Egypt e-Visa grants access to all tourist sites including Pyramids of Giza, Luxor temples, and the Valley of the Kings." },
      { q: "Is Egypt safe for Indian tourists?",              a: "Egypt's main tourist areas like Cairo, Luxor, Sharm El Sheikh, and Hurghada are well-secured. TheVisa recommends standard travel precautions." },
      { q: "Can I extend my Egypt visa?",                     a: "Yes. Egypt visa extensions can be arranged through the Mogamma office in Cairo or at a passport office in any major city." },
    ],
    reviews: [
      { name: "Varun Khanna",   rating: 5, date: "Feb 2026", title: "Egypt was jaw-dropping!",  body: "TheVisa got my Egypt visa in 4 days. Pyramids, Sphinx, Nile cruise — bucket list complete!" },
      { name: "Shalini Dubey",  rating: 5, date: "Jan 2026", title: "Seamless application",    body: "Surprisingly easy process. Team checked all my documents and visa came without any issues." },
      { name: "Rajiv Menon",    rating: 4, date: "Mar 2026", title: "Good overall experience", body: "Application was straightforward. The WhatsApp updates were very reassuring." },
      { name: "Trisha Goyal",   rating: 5, date: "Feb 2026", title: "Loved the service!",      body: "First time visiting Africa and TheVisa made the visa the easiest part of the whole trip!" },
    ],
    reviewScore: "4.8",
    reviewCount: "5,000+",
    authBadge: "Authorized Egypt e-Visa facilitation partner",
  },

  // ── MALAYSIA ───────────────────────────────────────────────────────────────
  malaysia: {
    heroImg: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=1600&q=85",
    heroAlt: "Kuala Lumpur Petronas Towers",
    country: "Malaysia",
    flag: "🇲🇾",
    tagline: "Truly Asia.",
    breadcrumb: ["Home", "Malaysia"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "37K+",     label: "Visas Processed" },
      { val: "24 Hours", label: "Duration" },
      { val: "4.9★",     label: "9K+ Reviews" },
      { val: "₹1,200",   label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "eNTRI / eVisa",
      stay: "15 days",
      validity: "3 months",
      entry: "Single",
      govFee: 1200,
      svcFee: 399,
      svcOrig: 599,
      guarantee: "Approval in 24 hours",
      expressFee: 1000,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",   desc: "Submit your passport details online — pay the Malaysia government fee upfront." },
      { icon: "🔍", step: "02", title: "Instant Review",     desc: "Our Malaysia specialists check all details for eNTRI/eVisa compliance." },
      { icon: "⚙️", step: "03", title: "Fast Processing",   desc: "We process through official Malaysia Immigration channels with priority handling." },
      { icon: "✅", step: "04", title: "eNTRI Delivered",   desc: "Receive your Malaysia eNTRI/eVisa via email within 24 hours." },
    ],
 
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your travel date",               img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent colour photo on white background",                         img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight from Malaysia",                            img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for your stay",                            img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
    ],
    destinations: [
      { name: "Kuala Lumpur", img: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=600&q=80" },
      { name: "Penang",       img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Langkawi",     img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "Malacca",      img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Kota Kinabalu", img: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=600&q=80" },
      { name: "Cameron Highlands", img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Malaysia?",             a: "Yes. Indian passport holders need an eNTRI or eVisa for Malaysia. TheVisa processes it within 24 guaranteed hours." },
      { q: "What is the Malaysia visa fee for Indians?",       a: "Government fee: ₹1,200 (upfront). TheVisa service fee: ₹399 (after approval). Total: ₹1,599. One of the most affordable visas." },
      { q: "What is the difference between eNTRI and eVisa?",  a: "eNTRI is for tourism for up to 15 days. eVisa (30 days) is for those who need a longer stay or visit for work or family." },
      { q: "How long can I stay in Malaysia on eNTRI?",        a: "eNTRI allows up to 15 days. For longer stays, apply for the eVisa which allows up to 30 days." },
      { q: "Can I visit Langkawi and Borneo on one visa?",     a: "Yes. A Malaysia visa covers all states including peninsular Malaysia, Sabah, and Sarawak in Borneo." },
      { q: "Is Malaysia safe and affordable for Indian tourists?", a: "Malaysia is one of the safest and most budget-friendly destinations for Indians, with a large Tamil and Hindi-speaking community." },
    ],
    reviews: [
      { name: "Rohan Shetty",   rating: 5, date: "Feb 2026", title: "eNTRI in 3 hours!",         body: "Applied at night, visa in my inbox by morning. Unbelievably fast! Malaysia was amazing." },
      { name: "Pallavi Joshi",  rating: 5, date: "Jan 2026", title: "Cheapest visa service",    body: "₹399 for a visa service? Absolute steal. Fast, professional, and seamless." },
      { name: "Deepak Rajan",   rating: 5, date: "Mar 2026", title: "Family trip sorted!",       body: "Applied for 4 people — all eNTRIs delivered overnight. KL trip was incredible." },
      { name: "Manasa Pillai",  rating: 4, date: "Feb 2026", title: "Great value for money",    body: "Fast processing, friendly team. Would definitely use TheVisa again for my next trip." },
    ],
    reviewScore: "4.9",
    reviewCount: "9,000+",
    authBadge: "Authorized Malaysia Immigration eNTRI partner",
  },

  // ── SINGAPORE ──────────────────────────────────────────────────────────────
  singapore: {
    heroImg: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1600&q=85",
    heroAlt: "Singapore Marina Bay Sands",
    country: "Singapore",
    flag: "🇸🇬",
    tagline: "The City of Lions.",
    breadcrumb: ["Home", "Singapore"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "22K+",   label: "Visas Processed" },
      { val: "7 Days", label: "Duration" },
      { val: "4.9★",   label: "5K+ Reviews" },
      { val: "₹4,800", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "SVP (Singapore Visitor Pass)",
      stay: "30 days",
      validity: "30 days",
      entry: "Single",
      govFee: 4800,
      svcFee: 999,
      svcOrig: 1500,
      guarantee: "Approval in 7 days",
      expressFee: 2000,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",     desc: "Fill the online form and upload documents — pay only the Singapore ICA government fee upfront." },
      { icon: "🔍", step: "02", title: "ICA Compliance Check", desc: "Expert review to ensure your application meets all ICA requirements before submission." },
      { icon: "⚙️", step: "03", title: "ICA Processing",      desc: "We track and coordinate with the Singapore Immigration & Checkpoints Authority." },
      { icon: "✅", step: "04", title: "Visa Delivered",       desc: "Receive your Singapore SVP via email — print and present on arrival." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",          desc: "Valid for at least 6 months beyond your intended stay",           img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",    desc: "Recent colour photo, white background, 35mm x 45mm",             img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",     desc: "Confirmed onward or return flight",                               img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",     desc: "Confirmed hotel or host accommodation letter",                    img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",    desc: "Last 3 months — minimum ₹75,000 recommended",                    img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "👔", title: "Employment Proof",  desc: "Salary slips or employer letter (for employed applicants)",       img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Marina Bay",      img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=600&q=80" },
      { name: "Sentosa Island",  img: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=600&q=80" },
      { name: "Gardens by Bay",  img: "https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=600&q=80" },
      { name: "Orchard Road",    img: "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=600&q=80" },
      { name: "Chinatown",       img: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=600&q=80" },
      { name: "Clarke Quay",     img: "https://images.unsplash.com/photo-1541417904950-b855846fe074?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Singapore?",           a: "Yes, Indian passport holders require a visitor visa to enter Singapore. Apply online through TheVisa — guaranteed in 7 days." },
      { q: "What is the Singapore visa fee for Indians?",     a: "Government fee: ₹4,800 (upfront). TheVisa service fee: ₹999 (after approval). Total: ₹5,799." },
      { q: "How long can I stay in Singapore?",               a: "A standard Singapore tourist visa allows a 30-day stay. Extensions are rarely granted — plan your trip accordingly." },
      { q: "What is the minimum bank balance required?",      a: "The ICA recommends at least SGD 1,000 (approx ₹62,000) per person per week of stay." },
      { q: "Can I apply for a Singapore visa for my family?", a: "Yes. TheVisa supports group and family applications — apply for all family members in one go." },
      { q: "Is Singapore visa on arrival available for Indians?", a: "No. Indian passport holders cannot get Singapore visa on arrival. An approved SVP must be obtained before travel." },
    ],
    reviews: [
      { name: "Kavya Suresh",  rating: 5, date: "Feb 2026", title: "Singapore trip sorted!",  body: "Got my Singapore visa in 6 days — one before the guarantee. Super smooth process." },
      { name: "Rajesh Kumar",  rating: 5, date: "Jan 2026", title: "Excellent service",       body: "Applied for family of 3. All three visas came together. Clear communication at every step." },
      { name: "Manish Gupta",  rating: 5, date: "Feb 2026", title: "Zero stress, total trust", body: "Used TheVisa after a bad experience elsewhere. Night and day difference." },
      { name: "Sunita Rao",    rating: 4, date: "Mar 2026", title: "Good but slightly pricey", body: "Service was great and professional. Cost a bit high but total peace of mind." },
    ],
    reviewScore: "4.9",
    reviewCount: "5,000+",
    authBadge: "Authorized agent for Singapore ICA applications",
  },

  // ── TURKEY ─────────────────────────────────────────────────────────────────
  turkey: {
    heroImg: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=1600&q=85",
    heroAlt: "Istanbul Hagia Sophia Turkey",
    country: "Turkey",
    flag: "🇹🇷",
    tagline: "Where East Meets West.",
    breadcrumb: ["Home", "Turkey"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "21K+",   label: "Visas Processed" },
      { val: "3 Days", label: "Duration" },
      { val: "4.8★",   label: "5K+ Reviews" },
      { val: "₹3,000", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "E-Visa (Tourist)",
      stay: "30 days",
      validity: "180 days",
      entry: "Single",
      govFee: 3000,
      svcFee: 799,
      svcOrig: 1199,
      guarantee: "Approval in 3 days",
      expressFee: 1500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",      desc: "Submit your passport online. Pay the Turkey government e-Visa fee upfront." },
      { icon: "🔍", step: "02", title: "Expert Document Check", desc: "Our Turkey visa specialists verify all details for 100% compliance." },
      { icon: "⚙️", step: "03", title: "E-Visa Processing",    desc: "We process through Turkey's official evisa.gov.tr portal with priority tracking." },
      { icon: "✅", step: "04", title: "E-Visa Delivered",     desc: "Receive your Turkey e-Visa via email — print and present at Istanbul airport." },
    ],
   
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 6 months from your travel date",               img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent colour photo on white background",                         img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight from Turkey",                              img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for your stay",                            img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
    ],
    destinations: [
      { name: "Istanbul",   img: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=600&q=80" },
      { name: "Cappadocia", img: "https://images.unsplash.com/photo-1570655652364-2e0a67455ac6?w=600&q=80" },
      { name: "Antalya",    img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Pamukkale",  img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Bodrum",     img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "Ephesus",    img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Turkey?",              a: "Yes. Indian passport holders require a Turkey e-Visa. TheVisa processes it in 3 guaranteed working days." },
      { q: "What is the Turkey e-Visa fee for Indians?",      a: "Government fee: ₹3,000 (upfront). TheVisa service fee: ₹799 (after approval). Total: ₹3,799." },
      { q: "How long can I stay in Turkey on an e-Visa?",     a: "The Turkey e-Visa allows up to 30 days per entry and is valid for 180 days from the date of issue." },
      { q: "Can I visit both European and Asian Istanbul?",   a: "Yes! Istanbul straddles two continents. A single Turkey visa lets you explore both the European and Asian sides." },
      { q: "Is Turkey visa valid for multiple entries?",      a: "Standard Turkey e-Visa is single entry. Multiple entry options are available — ask our team for details." },
      { q: "Is Turkey safe for Indian tourists?",             a: "Yes. Istanbul and major tourist areas are well-secured and very popular with Indian travellers." },
    ],
    reviews: [
      { name: "Preeti Kaur",    rating: 5, date: "Feb 2026", title: "Turkey was breathtaking!", body: "Cappadocia hot air balloon, Istanbul — all done in a week. Visa was ready in 2 days!" },
      { name: "Ankit Sharma",   rating: 5, date: "Jan 2026", title: "Fastest visa ever!",        body: "Turkey e-Visa arrived in under 48 hours. TheVisa is genuinely the best." },
      { name: "Namrata Singh",  rating: 4, date: "Mar 2026", title: "Very smooth process",       body: "Simple document requirements and excellent WhatsApp support. Highly recommend!" },
      { name: "Vivek Kulkarni", rating: 5, date: "Feb 2026", title: "Excellent from start to finish", body: "Second trip to Turkey — used TheVisa both times. Consistent and reliable." },
    ],
    reviewScore: "4.8",
    reviewCount: "5,000+",
    authBadge: "Authorized Turkey e-Visa facilitation partner",
  },

  // ── FRANCE ─────────────────────────────────────────────────────────────────
  france: {
    heroImg: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=85",
    heroAlt: "Paris Eiffel Tower at sunset",
    country: "France",
    flag: "🇫🇷",
    tagline: "Paris Awaits.",
    breadcrumb: ["Home", "France"],
    accentColor: "#1d4ed8",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "18K+",    label: "Visas Processed" },
  
      { val: "4.9★",    label: "3K+ Reviews" },
      { val: "₹7,200",  label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Schengen Visa (Type C)",
      stay: "90 days",
      validity: "180 days",
      entry: "Multiple",
      govFee: 7200,
      svcFee: 1499,
      svcOrig: 2299,
      guarantee: "Approval in 15 days",
      expressFee: 3500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",       desc: "Fill the form and upload documents — pay only the government fee upfront." },
      { icon: "🔍", step: "02", title: "Schengen Expert Review", desc: "Schengen specialists check every document for French consulate compliance." },
      { icon: "📅", step: "03", title: "VFS Appointment",        desc: "We book your VFS Global biometric appointment and submit your complete application." },
      { icon: "✅", step: "04", title: "Visa Delivered",         desc: "Your passport with Schengen sticker is couriered back to your doorstep." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 3 months beyond your intended stay",             img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent photo on white background, 35mm x 45mm",                    img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight booking",                                   img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for entire stay",                           img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",   desc: "Last 6 months — minimum ₹2,00,000 recommended",                    img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance", desc: "Minimum €30,000 coverage — mandatory for all Schengen visas",      img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Paris",      img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80" },
      { name: "Nice",       img: "https://images.unsplash.com/photo-1533929736458-ca588d08c8be?w=600&q=80" },
      { name: "Lyon",       img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Bordeaux",   img: "https://images.unsplash.com/photo-1565967511849-76a60a516170?w=600&q=80" },
      { name: "Versailles", img: "https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=600&q=80" },
      { name: "Marseille",  img: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for France?",                      a: "Yes. France is a Schengen country — Indians must apply for a Schengen visa. TheVisa guarantees processing in 15 days." },
      { q: "What is the France Schengen visa fee?",                   a: "Government fee: ₹7,200 (upfront). TheVisa service fee: ₹1,499 (after approval). Total: ₹8,699." },
      { q: "Can I travel to other Schengen countries on a France visa?", a: "Yes. A France Schengen visa covers all 27 Schengen countries, but France must be your primary destination." },
      { q: "How long can I stay in France on a Schengen visa?",       a: "Up to 90 days within any 180-day period across the entire Schengen Area." },
      { q: "Is travel insurance mandatory for France visa?",          a: "Yes. Minimum €30,000 medical coverage is mandatory for all Schengen applications. TheVisa can assist with trusted insurance providers." },
      { q: "Do I need to give biometrics for France visa?",           a: "Yes. First-time Schengen applicants must give fingerprints at a VFS Global centre. TheVisa books your appointment." },
    ],
    reviews: [
      { name: "Anita Krishnamurthy", rating: 5, date: "Feb 2026", title: "Paris dream came true!", body: "TheVisa handled my France Schengen application flawlessly. Got my visa in 13 days!" },
      { name: "Rohan Desai",         rating: 5, date: "Jan 2026", title: "Excellent from start to end", body: "Document checklist was thorough. Team guided me at every step. Truly professional." },
      { name: "Geeta Pillai",        rating: 4, date: "Mar 2026", title: "Smooth Schengen process", body: "France visa came in 12 days. TheVisa kept me updated throughout." },
      { name: "Nikhil Joshi",        rating: 5, date: "Feb 2026", title: "Stress-free Paris trip", body: "First Schengen visa and it was so easy. No VFS confusion at all — TheVisa handled everything." },
    ],
    reviewScore: "4.9",
    reviewCount: "3,000+",
    authBadge: "Authorized Schengen visa facilitation partner",
  },

  // ── JAPAN ──────────────────────────────────────────────────────────────────
  japan: {
    heroImg: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=1600&q=85",
    heroAlt: "Tokyo Japan at night",
    country: "Japan",
    flag: "🇯🇵",
    tagline: "Experience Japan — Zero Government Fee for Indians!",
    breadcrumb: ["Home", "Japan"],
    accentColor: "#be123c",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "29K+",    label: "Visas Processed" },
      { val: "10 Days", label: "Duration" },
      { val: "4.9★",    label: "4K+ Reviews" },
      { val: "₹0",      label: "Govt Fee (FREE!)" },
    ],
    visaCard: {
      type: "Single Entry Tourist Visa",
      stay: "15–30 days",
      validity: "3 months",
      entry: "Single",
      govFee: 0,
      svcFee: 1299,
      svcOrig: 1999,
      guarantee: "Approval in 10 days",
      expressFee: 2500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",       desc: "Submit all documents — Japan visa has ZERO government fee for Indians!" },
      { icon: "🔍", step: "02", title: "Itinerary & Doc Review", desc: "Japan consulate experts review your day-by-day itinerary, financials and all supporting documents." },
      { icon: "⚙️", step: "03", title: "Embassy Submission",    desc: "We submit to the Japanese Consulate/Embassy and track your application status." },
      { icon: "✅", step: "04", title: "Passport Returned",     desc: "Your passport with the Japan visa sticker is couriered directly to your door." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",               desc: "Valid for at least 6 months from your travel date",             img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",         desc: "Recent photo on white background, 45mm x 45mm",                 img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",          desc: "Confirmed return flight booking",                                img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🗓️", title: "Detailed Itinerary",    desc: "Day-by-day travel plan covering your entire Japan trip",         img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",         desc: "Last 6 months — minimum ₹2,50,000 recommended",                 img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "🏨", title: "Accommodation Details",  desc: "Day-by-day hotel or Airbnb booking confirmation",               img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
    ],
    destinations: [
      { name: "Tokyo",     img: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=600&q=80" },
      { name: "Kyoto",     img: "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=600&q=80" },
      { name: "Osaka",     img: "https://images.unsplash.com/photo-1590559899731-a382839e5549?w=600&q=80" },
      { name: "Hiroshima", img: "https://images.unsplash.com/photo-1570655652364-2e0a67455ac6?w=600&q=80" },
      { name: "Nara",      img: "https://images.unsplash.com/photo-1559592413-7cec4d0cae2b?w=600&q=80" },
      { name: "Hokkaido",  img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Japan?",            a: "Yes. Indians must apply for a Japan tourist visa. Great news: there is NO government fee for Indians!" },
      { q: "What is the Japan visa fee for Indians?",      a: "Government fee: ₹0 (FREE for Indians). TheVisa service fee: ₹1,299 (after approval only)." },
      { q: "How long can I stay in Japan?",                a: "A standard Japan tourist visa allows a 15-day or 30-day stay depending on consulate approval." },
      { q: "Do I need a detailed itinerary for Japan visa?", a: "Yes, the Japan consulate requires a detailed day-by-day itinerary. TheVisa provides a template and expert guidance." },
      { q: "How long does the Japan visa take?",           a: "TheVisa guarantees Japan visa processing in 10 working days. The consulate typically takes 5-7 business days to process." },
      { q: "Can I visit Japan during cherry blossom season?", a: "Absolutely! Cherry blossom season (late March to April) is one of the best times to visit Japan. Apply 2 months in advance for peak season." },
    ],
    reviews: [
      { name: "Kiran Bhat",     rating: 5, date: "Feb 2026", title: "Japan was unforgettable!", body: "Got Japan visa in 9 days — zero government fee! TheVisa's itinerary template was incredibly helpful." },
      { name: "Lavanya Iyer",   rating: 5, date: "Jan 2026", title: "Highly professional",     body: "Japan visa can be tricky but TheVisa made it seamless. Very detailed document checklist." },
      { name: "Abhishek Singh", rating: 5, date: "Mar 2026", title: "Best service for Japan!", body: "Third country visa with TheVisa — Japan. All three experiences have been exceptional." },
      { name: "Meena Pillai",   rating: 4, date: "Feb 2026", title: "Smooth process",          body: "Documents were perfect first time. Visa came in 8 days. Will use TheVisa for all future trips!" },
    ],
    reviewScore: "4.9",
    reviewCount: "4,000+",
    authBadge: "Authorized Japan visa application facilitator",
  },

  // ── CANADA ─────────────────────────────────────────────────────────────────
  canada: {
    heroImg: "https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=1600&q=85",
    heroAlt: "Vancouver Canada mountains",
    country: "Canada",
    flag: "🇨🇦",
    tagline: "The Great White North",
    breadcrumb: ["Home", "Canada"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "14K+",   label: "Visas Processed" },
      { val: "4 Weeks", label: "Processing Time" },
      { val: "4.8★",   label: "3K+ Reviews" },
      { val: "₹8,500", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Temporary Resident Visa (TRV)",
      stay: "Up to 6 months",
      validity: "10 years",
      entry: "Multiple",
      govFee: 8500,
      svcFee: 1799,
      svcOrig: 2699,
      guarantee: "Application in 48 hours",
      expressFee: 4000,
    },
    process: [
      { icon: "📝", step: "01", title: "IRCC Application",      desc: "We prepare and submit your complete Canada TRV application through IRCC online portal." },
      { icon: "🔍", step: "02", title: "Document Review",       desc: "Our Canada visa experts prepare a personalised document checklist and review everything." },
      { icon: "📊", step: "03", title: "Biometrics Booking",    desc: "We assist with VAC (Visa Application Centre) biometric appointment booking." },
      { icon: "✅", step: "04", title: "Visa Delivered",        desc: "Your passport with Canada visa stamp is couriered to your doorstep after IRCC approval." },
    ],
   
    essentials: [
      { icon: "🛂", title: "Passport",              desc: "Valid for at least 6 months beyond intended stay",              img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",        desc: "Recent photo on white background, 50mm x 70mm",                img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",        desc: "Last 6 months — minimum ₹5,00,000 recommended",                img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "👔", title: "Employment Letter",     desc: "Current employer letter with salary, designation, and leave details", img: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=400&q=80" },
      { icon: "✈️", title: "Travel Itinerary",      desc: "Detailed travel plan including flights and accommodation",       img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance",      desc: "Comprehensive travel insurance for the duration of your stay",  img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Toronto",      img: "https://images.unsplash.com/photo-1494522855154-9297ac14b55f?w=600&q=80" },
      { name: "Vancouver",    img: "https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=600&q=80" },
      { name: "Montreal",     img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Banff",        img: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=600&q=80" },
      { name: "Niagara Falls", img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Quebec City",  img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
    ],
    faqs: [
      { q: "How long does Canada visa take for Indians?",         a: "IRCC processing typically takes 3-8 weeks. TheVisa helps you submit the strongest application to minimize processing time." },
      { q: "What is the Canada TRV fee for Indians?",             a: "Government fee: ₹8,500 (non-refundable). TheVisa service fee: ₹1,799 (refunded if visa not approved). Total: ₹10,299." },
      { q: "Is the Canada visa valid for 10 years?",              a: "Multiple-entry TRV is often issued for 10 years or until passport expiry. Duration is at IRCC's discretion." },
      { q: "Do I need biometrics for a Canada visa?",             a: "Yes. First-time applicants must provide biometrics at a Visa Application Centre (VAC). TheVisa books your appointment." },
      { q: "What is the minimum bank balance for Canada visa?",   a: "IRCC looks for proof of sufficient funds. Minimum ₹5,00,000 is recommended, though more is better for family trips." },
      { q: "Can I work in Canada on a tourist visa?",             a: "No. A TRV (tourist visa) does not permit work in Canada. You must apply for a separate work permit for employment." },
    ],
    reviews: [
      { name: "Gaurav Khanna",  rating: 5, date: "Feb 2026", title: "Canada in the first attempt!", body: "TheVisa's application was thorough. Got a 10-year Canada visa on first try. Banff was spectacular!" },
      { name: "Priti Menon",    rating: 5, date: "Jan 2026", title: "Expert guidance throughout",   body: "Canada visa can be daunting. TheVisa made it simple. Document checklist was spot on." },
      { name: "Ravi Sundaram",  rating: 4, date: "Mar 2026", title: "Solid service",                body: "Application submitted quickly. Visa came in 5 weeks. Very clear communication from the team." },
      { name: "Meghna Joshi",   rating: 5, date: "Feb 2026", title: "Family Canada visa done!",    body: "Applied for 3 family members. All got 10-year Canada visas! TheVisa's preparation was exceptional." },
    ],
    reviewScore: "4.8",
    reviewCount: "3,000+",
    authBadge: "Authorized Canada TRV application facilitation partner",
  },

  // ── AUSTRALIA ──────────────────────────────────────────────────────────────
  australia: {
    heroImg: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=1600&q=85",
    heroAlt: "Sydney Opera House Australia",
    country: "Australia",
    flag: "🇦🇺",
    tagline: "G'Day Mate.",
    breadcrumb: ["Home", "Australia"],
    accentColor: "#d97706",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "20K+",    label: "Visas Processed" },
      { val: "4 Weeks", label: "Processing Time" },
      { val: "4.8★",    label: "4K+ Reviews" },
      { val: "₹11,500", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Visitor Visa (Subclass 600)",
      stay: "Up to 3 months",
      validity: "12 months",
      entry: "Multiple",
      govFee: 11500,
      svcFee: 1799,
      svcOrig: 2699,
      guarantee: "Application in 48 hours",
      expressFee: 4000,
    },
    process: [
      { icon: "📝", step: "01", title: "ImmiAccount Setup",      desc: "We create and manage your Australian ImmiAccount and complete your Subclass 600 application." },
      { icon: "🔍", step: "02", title: "Document Preparation",   desc: "Our Australia visa experts prepare a tailored document pack for your application profile." },
      { icon: "⚙️", step: "03", title: "Online Submission",     desc: "We submit your complete application online through the Department of Home Affairs portal." },
      { icon: "✅", step: "04", title: "Visa Grant Notice",      desc: "Your Australia visa grant notice is emailed to you — no sticker needed, completely digital." },
    ],
   
    essentials: [
      { icon: "🛂", title: "Passport",              desc: "Valid for the entire duration of your planned stay",              img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",        desc: "Recent digital passport photo meeting Australian standards",      img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",        desc: "Last 6 months — minimum ₹5,00,000 recommended per person",       img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "👔", title: "Employment Proof",      desc: "Employer letter confirming your employment and approved leave",   img: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=400&q=80" },
      { icon: "✈️", title: "Travel Itinerary",      desc: "Detailed trip plan with flight and accommodation details",        img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance",      desc: "Comprehensive health and travel coverage for your Australia trip", img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Sydney",       img: "https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?w=600&q=80" },
      { name: "Melbourne",    img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Gold Coast",   img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Great Barrier Reef", img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "Uluru",        img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Brisbane",     img: "https://images.unsplash.com/photo-1555921015-5532091f6026?w=600&q=80" },
    ],
    faqs: [
      { q: "How long does Australia visa take for Indians?",        a: "Australia Subclass 600 typically processes in 4-12 weeks. TheVisa ensures your application is submitted perfectly to minimize delays." },
      { q: "What is the Australia tourist visa fee for Indians?",   a: "Government fee: ₹11,500 (non-refundable). TheVisa service fee: ₹1,799 (refunded if not granted). Total: ₹13,299." },
      { q: "Do I need biometrics for Australia visa?",              a: "Biometrics are typically waived for Indian applicants for Subclass 600, but may be requested by the Department of Home Affairs." },
      { q: "Is Australia tourist visa multiple entry?",             a: "Yes! The Subclass 600 visitor visa is typically issued as multiple entry, valid for 12 months from issue date." },
      { q: "What is the minimum bank balance for Australia visa?",  a: "Department of Home Affairs recommends AUD 5,000+ (approx ₹2,70,000) for a standard visit. Family trips require more." },
      { q: "Can I extend my Australia tourist visa?",               a: "Extensions can be applied for within Australia before the visa expires. TheVisa can assist with the extension application." },
    ],
    reviews: [
      { name: "Suresh Nambiar", rating: 5, date: "Feb 2026", title: "Australia approved!",           body: "First attempt approval for Australia — TheVisa's document prep was extraordinary. Sydney was mind-blowing." },
      { name: "Aruna Pillai",   rating: 5, date: "Jan 2026", title: "Thorough and professional",    body: "Complex employment situation but TheVisa handled it perfectly. Visa granted in 5 weeks." },
      { name: "Deepak Verma",   rating: 4, date: "Mar 2026", title: "Solid visa service",           body: "Application was submitted in 48 hours as promised. Clear communication throughout." },
      { name: "Smita Joshi",    rating: 5, date: "Feb 2026", title: "Gold Coast was worth every penny!", body: "3-week Australia trip with family. All 4 visas approved together. TheVisa was superb!" },
    ],
    reviewScore: "4.8",
    reviewCount: "4,000+",
    authBadge: "Authorized Australia Subclass 600 facilitation partner",
  },

  // ── NEW ZEALAND ────────────────────────────────────────────────────────────
  "new-zealand": {
    heroImg: "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=1600&q=85",
    heroAlt: "New Zealand fjords and mountains",
    country: "New Zealand",
    flag: "🇳🇿",
    tagline: "Land of the Long White Cloud.",
    breadcrumb: ["Home", "New Zealand"],
    accentColor: "#059669",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "11K+",   label: "Visas Processed" },
      { val: "3 Weeks", label: "Processing Time" },
      { val: "4.8★",   label: "2K+ Reviews" },
      { val: "₹9,000", label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Visitor Visa (INZ 1017)",
      stay: "Up to 9 months",
      validity: "12 months",
      entry: "Multiple",
      govFee: 9000,
      svcFee: 1699,
      svcOrig: 2499,
      guarantee: "Application in 48 hours",
      expressFee: 3500,
    },
    process: [
      { icon: "📝", step: "01", title: "RealMe Account Setup",  desc: "We prepare your complete New Zealand INZ 1017 visa application online through Immigration NZ." },
      { icon: "🔍", step: "02", title: "Document Preparation",  desc: "Our NZ visa specialists prepare your document pack for Immigration NZ compliance." },
      { icon: "⚙️", step: "03", title: "Online Submission",    desc: "We submit your application to Immigration New Zealand and track progress." },
      { icon: "✅", step: "04", title: "Visa Granted",          desc: "New Zealand visitor visa is issued digitally — emailed to you directly." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",          desc: "Valid for at least 3 months beyond your intended departure from NZ",  img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",    desc: "Recent digital passport photo meeting NZ immigration standards",       img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",    desc: "Last 6 months — minimum NZD 1,000/month (approx ₹50,000/month)",      img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",     desc: "Confirmed return or onward travel booking",                           img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance",  desc: "Comprehensive coverage recommended for your entire NZ stay",          img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Auckland",         img: "https://images.unsplash.com/photo-1507699622108-4be3abd695ad?w=600&q=80" },
      { name: "Queenstown",       img: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=600&q=80" },
      { name: "Milford Sound",    img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Rotorua",          img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Wellington",       img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Bay of Islands",   img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for New Zealand?",            a: "Yes. Indian passport holders need a visitor visa for New Zealand. TheVisa handles your INZ 1017 application." },
      { q: "What is the NZ visa fee for Indians?",               a: "Government fee: ₹9,000 (non-refundable). TheVisa service fee: ₹1,699 (refunded if not approved). Total: ₹10,699." },
      { q: "How long can I stay in New Zealand?",                a: "NZ visitor visa allows up to 9 months stay — one of the most generous in the region." },
      { q: "Is New Zealand visa completely digital?",            a: "Yes! New Zealand issues a fully digital visa — no sticker or stamp. Your visa is linked to your passport electronically." },
      { q: "Can I visit both North and South Island on one visa?", a: "Absolutely. One NZ visitor visa gives you access to explore both islands — from Auckland to Queenstown and Milford Sound." },
      { q: "What is the best time to visit New Zealand?",        a: "December to February (NZ summer) for warm weather. June to August for skiing in Queenstown. Spring (Sep–Nov) is ideal for gardens and hiking." },
    ],
    reviews: [
      { name: "Arun Krishnan",   rating: 5, date: "Feb 2026", title: "NZ was magnificent!",         body: "Milford Sound, Queenstown — the visa was the easiest part thanks to TheVisa. Stunning country!" },
      { name: "Divya Iyer",      rating: 5, date: "Jan 2026", title: "Flawless visa process",       body: "Digital NZ visa granted in 16 days. TheVisa's doc preparation was excellent." },
      { name: "Raghav Pandya",   rating: 4, date: "Mar 2026", title: "Recommended for NZ",          body: "Professional service. Got my NZ visa without any issues. Great WhatsApp support." },
      { name: "Shefali Mehta",   rating: 5, date: "Feb 2026", title: "Honeymoon sorted!",           body: "NZ honeymoon — both our visas sorted in 17 days. TheVisa was responsive and thorough." },
    ],
    reviewScore: "4.8",
    reviewCount: "2,000+",
    authBadge: "Authorized Immigration NZ facilitation partner",
  },

  // ── GERMANY ────────────────────────────────────────────────────────────────
  germany: {
    heroImg: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=1600&q=85",
    heroAlt: "Neuschwanstein Castle Germany",
    country: "Germany",
    flag: "🇩🇪",
    tagline: "Efficiency & Elegance .",
    breadcrumb: ["Home", "Germany"],
    accentColor: "#d97706",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "15K+",    label: "Visas Processed" },
      { val: "15 Days", label: "Duration" },
      { val: "4.9★",    label: "3K+ Reviews" },
      { val: "₹7,200",  label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Schengen Visa (Type C)",
      stay: "90 days",
      validity: "180 days",
      entry: "Multiple",
      govFee: 7200,
      svcFee: 1499,
      svcOrig: 2299,
      guarantee: "Approval in 15 days",
      expressFee: 3500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",       desc: "Fill the form and upload documents — pay only the German consulate government fee upfront." },
      { icon: "🔍", step: "02", title: "Schengen Expert Review", desc: "German consulate specialists verify every document with precision." },
      { icon: "📅", step: "03", title: "VFS Appointment",        desc: "We book your VFS Global biometric appointment and submit your complete file." },
      { icon: "✅", step: "04", title: "Passport Delivered",     desc: "Your passport with Schengen sticker is couriered back to your home." },
    ],
  
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 3 months beyond your intended return",           img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent photo on white background, 35mm x 45mm",                    img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight booking",                                   img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for entire stay",                           img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",   desc: "Last 6 months — minimum ₹2,50,000 recommended",                    img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance", desc: "Minimum €30,000 coverage — mandatory for all Schengen visas",      img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Berlin",        img: "https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=600&q=80" },
      { name: "Munich",        img: "https://images.unsplash.com/photo-1529927120475-c2a81aa28f6b?w=600&q=80" },
      { name: "Hamburg",       img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Frankfurt",     img: "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=600&q=80" },
      { name: "Heidelberg",    img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Neuschwanstein", img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Germany?",              a: "Yes. Germany is a Schengen country. Indians must apply for a Schengen visa. TheVisa guarantees processing in 15 days." },
      { q: "What is the Germany visa fee for Indians?",        a: "Government fee: ₹7,200 (upfront). TheVisa service fee: ₹1,499 (after approval). Total: ₹8,699." },
      { q: "Can I travel to other Schengen countries?",        a: "Yes. A German Schengen visa covers all 27 Schengen countries, but Germany must be your primary or first destination." },
      { q: "How long can I stay in Germany?",                  a: "Up to 90 days within any 180-day period across the Schengen Area." },
      { q: "Is the Oktoberfest a good time to visit Germany?", a: "Yes! Munich's Oktoberfest runs in late September to early October. Apply at least 2 months early as demand is very high." },
      { q: "Do I need a cover letter for Germany visa?",       a: "A well-written cover letter explaining your trip purpose significantly improves approval chances. TheVisa's team prepares one for you." },
    ],
    reviews: [
      { name: "Vikram Bose",    rating: 5, date: "Feb 2026", title: "Germany exceeded expectations!", body: "Berlin, Munich, Neuschwanstein — all in 12 days. TheVisa made the Schengen process effortless." },
      { name: "Asha Srinivasan", rating: 5, date: "Jan 2026", title: "Smooth Schengen process",      body: "Got my Germany Schengen visa in 11 days. VFS appointment was well-coordinated by TheVisa." },
      { name: "Naresh Patel",   rating: 4, date: "Mar 2026", title: "Very professional",             body: "Document list was detailed and accurate. No surprises at VFS. Visa came on time." },
      { name: "Rekha Iyer",     rating: 5, date: "Feb 2026", title: "First Schengen nailed!",        body: "Was nervous about my first Schengen application. TheVisa guided me perfectly throughout." },
    ],
    reviewScore: "4.9",
    reviewCount: "3,000+",
    authBadge: "Authorized Schengen visa facilitation partner",
  },

  // ── SPAIN ──────────────────────────────────────────────────────────────────
  spain: {
    heroImg: "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=1600&q=85",
    heroAlt: "Barcelona Sagrada Familia Spain",
    country: "Spain",
    flag: "🇪🇸",
    tagline: "Fiesta, Flamenco & Tapas.",
    breadcrumb: ["Home", "Spain"],
    accentColor: "#dc2626",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "13K+",    label: "Visas Processed" },
      { val: "15 Days", label: "Duration" },
      { val: "4.8★",    label: "3K+ Reviews" },
      { val: "₹7,200",  label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Schengen Visa (Type C)",
      stay: "90 days",
      validity: "180 days",
      entry: "Multiple",
      govFee: 7200,
      svcFee: 1499,
      svcOrig: 2299,
      guarantee: "Approval in 15 days",
      expressFee: 3500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",       desc: "Fill the form and upload documents — pay only the Spanish consulate government fee upfront." },
      { icon: "🔍", step: "02", title: "Schengen Expert Review", desc: "Spain Schengen specialists verify every document for consulate compliance." },
      { icon: "📅", step: "03", title: "VFS Appointment",        desc: "We book your VFS Global biometric appointment and submit your complete file." },
      { icon: "✅", step: "04", title: "Passport Delivered",     desc: "Your passport with Spain Schengen sticker is couriered back to your home." },
    ],
  
    essentials: [
  {
    icon: "🛂",
    title: "Passport",
    desc: "Valid for at least 3 months beyond your intended return",
    img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80"
  },
  {
    icon: "🖼️",
    title: "Passport Photo",
    desc: "Recent photo on white background, 35mm × 45mm",
    img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80"
  },
  {
    icon: "✈️",
    title: "Return Ticket",
    desc: "Confirmed return flight booking",
    img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80"
  },
  {
    icon: "🏨",
    title: "Hotel Booking",
    desc: "Confirmed accommodation for entire stay",
    img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80"
  },
  {
    icon: "💳",
    title: "Bank Statement",
    desc: "Last 6 months — minimum ₹2,00,000 recommended",
    img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80"
  },
  {
    icon: "📋",
    title: "Travel Insurance",
    desc: "Minimum €30,000 coverage — mandatory for all Schengen visas",
    img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80"
  },
],
    destinations: [
      { name: "Barcelona",   img: "https://images.unsplash.com/photo-1539037116277-4db20889f2d4?w=600&q=80" },
      { name: "Madrid",      img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Seville",     img: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=600&q=80" },
      { name: "Granada",     img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
      { name: "Ibiza",       img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "Valencia",    img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Spain?",                   a: "Yes. Spain is Schengen — Indians must apply for a Spain Schengen visa. TheVisa processes in 15 guaranteed days." },
      { q: "What is the Spain visa fee for Indians?",             a: "Government fee: ₹7,200 (upfront). TheVisa service fee: ₹1,499 (after approval). Total: ₹8,699." },
      { q: "Can I travel to other Schengen countries?",           a: "Yes. Spain Schengen covers all 27 Schengen states, but Spain should be your primary destination." },
      { q: "How long can I stay in Spain?",                       a: "Up to 90 days within any 180-day period across the entire Schengen Area." },
      { q: "What is the best time to visit Spain?",               a: "Spring (March-May) and autumn (Sep-Nov) offer the best weather. Summer can be hot, especially in Seville and Madrid." },
      { q: "Do I need to visit the Spanish consulate in person?", a: "You must give biometrics at VFS Global once. TheVisa books your appointment and prepares your complete file in advance." },
    ],
    reviews: [
      { name: "Priya Kapoor",   rating: 5, date: "Feb 2026", title: "Barcelona blew my mind!",     body: "Sagrada Familia, Gaudí, tapas — all amazing. TheVisa sorted the Schengen visa perfectly." },
      { name: "Arun Das",       rating: 5, date: "Jan 2026", title: "Excellent Schengen service",  body: "Spain visa approved in 13 days. Document checklist was spot-on. No surprises at VFS." },
      { name: "Kavita Sharma",  rating: 4, date: "Mar 2026", title: "Smooth and professional",    body: "Straightforward process. WhatsApp updates were very helpful. Visa on time." },
      { name: "Tanmay Roy",     rating: 5, date: "Feb 2026", title: "Spain trip made perfect",    body: "Used TheVisa for France last year and Spain this year. Consistent quality both times." },
    ],
    reviewScore: "4.8",
    reviewCount: "3,000+",
    authBadge: "Authorized Schengen visa facilitation partner",
  },

  // ── ITALY ──────────────────────────────────────────────────────────────────
  italy: {
    heroImg: "https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=1600&q=85",
    heroAlt: "Rome Colosseum Italy",
    country: "Italy",
    flag: "🇮🇹",
    tagline: "La Dolce Vita.",
    breadcrumb: ["Home", "Italy"],
    accentColor: "#059669",
    navLinks: ["Info", "Journey", "Process", "Essential", "Why Us", "FAQ"],
    stats: [
      { val: "16K+",    label: "Visas Processed" },
      { val: "15 Days", label: "Duration" },
      { val: "4.9★",    label: "3K+ Reviews" },
      { val: "₹7,200",  label: "Govt Fee Only" },
    ],
    visaCard: {
      type: "Schengen Visa (Type C)",
      stay: "90 days",
      validity: "180 days",
      entry: "Multiple",
      govFee: 7200,
      svcFee: 1499,
      svcOrig: 2299,
      guarantee: "Approval in 15 days",
      expressFee: 3500,
    },
    process: [
      { icon: "📝", step: "01", title: "Apply on TheVisa",       desc: "Fill the form and upload documents — pay only the Italian consulate government fee upfront." },
      { icon: "🔍", step: "02", title: "Schengen Expert Review", desc: "Italy Schengen specialists verify every document for consulate requirements." },
      { icon: "📅", step: "03", title: "VFS Appointment",        desc: "We book your VFS Global biometric appointment and submit your full file." },
      { icon: "✅", step: "04", title: "Passport Delivered",     desc: "Your passport with Italy Schengen sticker is couriered back to your doorstep." },
    ],
    whyUs: [
      { icon: "🛡️", title: "15-Day Approval",    desc: "Italy Schengen visa guaranteed in 15 working days or service fee waived." },
      { icon: "🇪🇺", title: "27-Country Access",  desc: "One Italy Schengen visa covers all 27 Schengen member states." },
      { icon: "📅", title: "VFS Appointment",      desc: "We handle your VFS Global appointment booking end-to-end." },
      { icon: "🔒", title: "100% Refund Policy",  desc: "Rejected? Full service fee refunded — guaranteed." },
      { icon: "📦", title: "Doorstep Delivery",   desc: "Passport with visa sticker delivered via tracked courier." },
      { icon: "✅", title: "Italy Specialists",   desc: "Italian consulate experts with 95%+ first-attempt approval rate." },
    ],
    essentials: [
      { icon: "🛂", title: "Passport",         desc: "Valid for at least 3 months beyond your intended return",           img: "https://images.unsplash.com/photo-1575505586569-646b2ca898fc?w=400&q=80" },
      { icon: "🖼️", title: "Passport Photo",   desc: "Recent photo on white background, 35mm x 45mm",                    img: "https://images.unsplash.com/photo-1554151228-14d9def656e4?w=400&q=80" },
      { icon: "✈️", title: "Return Ticket",    desc: "Confirmed return flight booking",                                   img: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=400&q=80" },
      { icon: "🏨", title: "Hotel Booking",    desc: "Confirmed accommodation for entire stay",                           img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80" },
      { icon: "💳", title: "Bank Statement",   desc: "Last 6 months — minimum ₹2,00,000 recommended",                    img: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&q=80" },
      { icon: "📋", title: "Travel Insurance", desc: "Minimum €30,000 coverage — mandatory for all Schengen visas",      img: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=400&q=80" },
    ],
    destinations: [
      { name: "Rome",        img: "https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=600&q=80" },
      { name: "Venice",      img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80" },
      { name: "Florence",    img: "https://images.unsplash.com/photo-1570168007204-dfb528c6958f?w=600&q=80" },
      { name: "Milan",       img: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80" },
      { name: "Amalfi Coast", img: "https://images.unsplash.com/photo-1552733407-5d5c46c3bb3b?w=600&q=80" },
      { name: "Cinque Terre", img: "https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=600&q=80" },
    ],
    faqs: [
      { q: "Do Indians need a visa for Italy?",                   a: "Yes. Italy is part of the Schengen Area — Indians must apply for an Italy Schengen visa. TheVisa processes in 15 guaranteed days." },
      { q: "What is the Italy visa fee for Indians?",             a: "Government fee: ₹7,200 (upfront). TheVisa service fee: ₹1,499 (after approval). Total: ₹8,699." },
      { q: "Can I travel to other Schengen countries?",           a: "Yes. Italy Schengen covers all 27 Schengen states, but Italy should be your primary destination." },
      { q: "How long can I stay in Italy?",                       a: "Up to 90 days within any 180-day period across the entire Schengen Area." },
      { q: "Is Rome or Venice the best entry point to Italy?",    a: "Rome (Fiumicino) and Milan (Malpensa) are the most popular international entry points. Venice has a smaller airport mainly for European flights." },
      { q: "What is the best time to visit Italy from India?",    a: "April-June and September-October offer the best weather and fewer crowds. July-August is peak tourist season — book well in advance." },
    ],
    reviews: [
      { name: "Sonia Malhotra", rating: 5, date: "Feb 2026", title: "Italy was a dream!",         body: "Rome, Venice, Amalfi Coast — all covered in 10 days. TheVisa made the Schengen visa stress-free!" },
      { name: "Pradeep Kumar",  rating: 5, date: "Jan 2026", title: "Excellent Schengen prep",   body: "Italy visa approved in 12 days. Complete document guidance from TheVisa — zero issues at VFS." },
      { name: "Anjali Singh",   rating: 4, date: "Mar 2026", title: "Smooth and professional",   body: "Process was clear and organized. Visa arrived before my Italy trip with days to spare." },
      { name: "Ronak Shah",     rating: 5, date: "Feb 2026", title: "Flawless from start to end!", body: "Applied for Italy for my honeymoon. TheVisa handled everything perfectly. Italy was magical!" },
    ],
    reviewScore: "4.9",
    reviewCount: "3,000+",
    authBadge: "Authorized Schengen visa facilitation partner",
  },
};
export function getVisaData(visaId) {
  const base = DATA[visaId] || DATA["uae"];
  const id   = DATA[visaId] ? visaId : "uae";
  const baseLinks = base.navLinks ?? ["Info", "Process", "Why Us", "Essentials", "FAQ"];
  const navLinks  = baseLinks.includes("Journey")
    ? baseLinks
    : baseLinks.reduce((acc, l) => { acc.push(l); if (l === "Process") acc.push("Journey"); return acc; }, []);
  return {
    ...base,
    navLinks,
    approvalRate:      APPROVAL_RATES[id]         ?? 95,
    refundPolicy:      REFUND_POLICIES[id]         ?? REFUND_POLICIES["uae"],
    visaTypes:         VISA_TYPES[id]              ?? [],
    currency:          CURRENCY_CONFIG[id]         ?? { code: "USD", symbol: "$", rate: 83.5, name: "US Dollar" },
    journeySteps:      VISA_JOURNEY[id]            ?? VISA_JOURNEY["uae"],
    govFeeIncreasePct: GOV_FEE_INCREASE_PCT[id]   ?? 0,      // ← ADD
    govFeePageLink:    GOV_FEE_PAGE_LINKS[id]      ?? null,   // ← ADD
    refundJourneyStep: REFUND_JOURNEY_STEP[id]     ?? null,   // ← ADD
  };
}
// ─── HELPER for getVisapagegData (used by booking.jsx) ─────────────────────
export function getVisapagegData(visaId) {
  return getVisaData(visaId);
}

export default DATA;

// ─────────────────────────────────────────────────────────────────────────────
// PATCH FILE: bookingdata_additions.js
// Add these exports/constants to bookingdata.js
// Then merge the PER_COUNTRY additions into each country's DATA[id] object
// ─────────────────────────────────────────────────────────────────────────────

// ── 1. CURRENCY CONFIG ────────────────────────────────────────────────────────
// Add this near the top of bookingdata.js (after imports)

export const CURRENCY_CONFIG = {
  uae:         { code: "AED", symbol: "AED", rate: 22.84,  name: "UAE Dirham" },
  thailand:    { code: "THB", symbol: "฿",   rate: 2.27,   name: "Thai Baht" },
  switzerland: { code: "EUR", symbol: "€",   rate: 92.5,   name: "Euro" },
  vietnam:     { code: "USD", symbol: "$",   rate: 83.5,   name: "US Dollar" },
  indonesia:   { code: "IDR", symbol: "Rp",  rate: 0.0052, name: "Indonesian Rupiah" },
  maldives:    { code: "USD", symbol: "$",   rate: 83.5,   name: "US Dollar" },
  usa:         { code: "USD", symbol: "$",   rate: 83.5,   name: "US Dollar" },
  "hong-kong": { code: "HKD", symbol: "HK$", rate: 10.68,  name: "Hong Kong Dollar" },
  egypt:       { code: "USD", symbol: "$",   rate: 83.5,   name: "US Dollar" },
  malaysia:    { code: "MYR", symbol: "RM",  rate: 17.85,  name: "Malaysian Ringgit" },
  singapore:   { code: "SGD", symbol: "S$",  rate: 61.8,   name: "Singapore Dollar" },
  turkey:      { code: "USD", symbol: "$",   rate: 83.5,   name: "US Dollar" },
  france:      { code: "EUR", symbol: "€",   rate: 92.5,   name: "Euro" },
  japan:       { code: "JPY", symbol: "¥",   rate: 0.554,  name: "Japanese Yen" },
  canada:      { code: "CAD", symbol: "CA$", rate: 61.2,   name: "Canadian Dollar" },
  australia:   { code: "AUD", symbol: "A$",  rate: 54.3,   name: "Australian Dollar" },
  "new-zealand":{ code: "NZD", symbol: "NZ$", rate: 50.1,  name: "New Zealand Dollar" },
  germany:     { code: "EUR", symbol: "€",   rate: 92.5,   name: "Euro" },
  spain:       { code: "EUR", symbol: "€",   rate: 92.5,   name: "Euro" },
  italy:       { code: "EUR", symbol: "€",   rate: 92.5,   name: "Euro" },
};

// Helper: convert INR  local currency amount (for display)
export function inrToLocal(inrAmount, visaId) {
  const cfg = CURRENCY_CONFIG[visaId];
  if (!cfg) return null;
  const localAmt = inrAmount / cfg.rate;
  // Format nicely
  if (cfg.code === "JPY" || cfg.code === "IDR") {
    return { amount: Math.round(localAmt), symbol: cfg.symbol, code: cfg.code, name: cfg.name };
  }
  return { amount: parseFloat(localAmt.toFixed(2)), symbol: cfg.symbol, code: cfg.code, name: cfg.name };
}


// ── 2. VISA TYPES CONFIG ─────────────────────────────────────────────────────
// Schengen countries share types A / C / D
const SCHENGEN_TYPES = [
  {
    id:       "type-c",
    label:    "Type C — Short Stay (Tourist / Business)",
    desc:     "Up to 90 days within 180 days. Covers tourism, business visits, family visits.",
    stay:     "Up to 90 days",
    validity: "180 days",
    entry:    "Single or Multiple",
    govFeeMultiplier: 1,      // base
    svcFeeMultiplier: 1,
  },
  {
    id:       "type-a",
    label:    "Type A — Airport Transit",
    desc:     "Transit through a Schengen airport without entering the Schengen Area.",
    stay:     "Transit only",
    validity: "Single journey",
    entry:    "Single",
    govFeeMultiplier: 0.83,   // slightly lower
    svcFeeMultiplier: 0.80,
  },
  {
    id:       "type-d",
    label:    "Type D — Long Stay (Study / Work)",
    desc:     "More than 90 days. Required for study programmes, employment or family reunification.",
    stay:     "Over 90 days",
    validity: "Duration of stay",
    entry:    "Multiple",
    govFeeMultiplier: 1.28,   // higher
    svcFeeMultiplier: 1.40,
  },
];

export const VISA_TYPES = {
  // ── Non-Schengen countries ─────────────────────────────────────────────
  uae: [
    { id: "tourist",  label: "Tourist / Visit Visa",   desc: "Leisure, tourism or family visits.",                  stay: "30 days",       validity: "60 days",   entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "transit",  label: "Transit Visa",            desc: "Connecting through Dubai/Abu Dhabi airports.",        stay: "96 hours",      validity: "14 days",   entry: "Single",   govFeeMultiplier: 0.55, svcFeeMultiplier: 0.65 },
    { id: "business", label: "Business Visa",           desc: "Business meetings, conferences or trade events.",     stay: "30 days",       validity: "60 days",   entry: "Multiple", govFeeMultiplier: 1.15, svcFeeMultiplier: 1.20 },
    { id: "medical",  label: "Medical Visa",            desc: "Medical treatment at UAE healthcare facilities.",     stay: "90 days",       validity: "6 months",  entry: "Multiple", govFeeMultiplier: 1.25, svcFeeMultiplier: 1.30 },
  ],
  thailand: [
    { id: "tourist",  label: "Tourist Visa (TR)",       desc: "Standard tourism and leisure.",                       stay: "30 days",       validity: "90 days",   entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "metv",     label: "Multiple Entry Tourist",  desc: "Six-month visa with 60 days per entry.",              stay: "60 days/entry", validity: "6 months",  entry: "Multiple", govFeeMultiplier: 1.95, svcFeeMultiplier: 1.80 },
    { id: "business", label: "Non-Immigrant B (Business)", desc: "Business activities, employment or work permit.", stay: "90 days",       validity: "3 months",  entry: "Single",   govFeeMultiplier: 1.40, svcFeeMultiplier: 1.35 },
    { id: "student",  label: "Non-Immigrant ED (Education)", desc: "Study at Thai schools or language institutes.", stay: "90 days",       validity: "3 months",  entry: "Single",   govFeeMultiplier: 1.40, svcFeeMultiplier: 1.35 },
    { id: "transit",  label: "Transit Visa",            desc: "Passing through Thailand.",                           stay: "30 days",       validity: "6 months",  entry: "Single",   govFeeMultiplier: 0.60, svcFeeMultiplier: 0.65 },
  ],
  vietnam: [
    { id: "tourist-single", label: "E-Visa — Single Entry",   desc: "Single entry tourism visa.",             stay: "90 days", validity: "90 days", entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "tourist-multi",  label: "E-Visa — Multiple Entry", desc: "Multiple entry within validity period.", stay: "90 days", validity: "90 days", entry: "Multiple", govFeeMultiplier: 1.40, svcFeeMultiplier: 1.30 },
    { id: "business",       label: "Business Visa",           desc: "Business meetings and trade.",           stay: "90 days", validity: "90 days", entry: "Multiple", govFeeMultiplier: 1.50, svcFeeMultiplier: 1.40 },
    { id: "transit",        label: "Transit Visa",            desc: "Transiting through Vietnamese airports.",stay: "24 hours",validity: "30 days", entry: "Single",   govFeeMultiplier: 0.50, svcFeeMultiplier: 0.60 },
  ],
  indonesia: [
    { id: "evoa",     label: "e-VOA (Tourist)",          desc: "Pre-approved Visa on Arrival for tourism.",           stay: "30 days", validity: "30 days",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "business", label: "Business Visa on Arrival", desc: "VOA for business meetings.",                          stay: "30 days", validity: "30 days",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1.20 },
    { id: "student",  label: "Social / Cultural Visa",   desc: "Study, internship or volunteer programmes.",          stay: "60 days", validity: "6 months", entry: "Multiple", govFeeMultiplier: 1.80, svcFeeMultiplier: 1.50 },
  ],
  maldives: [
    { id: "voa",      label: "Free Visa on Arrival",     desc: "Standard free 30-day tourist visa.",                  stay: "30 days", validity: "30 days",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "extended", label: "Extended Stay (90 days)",  desc: "Extended stay for longer visits.",                    stay: "90 days", validity: "90 days",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1.60 },
  ],
  usa: [
    { id: "b2",       label: "B-2 Tourist Visa",         desc: "Tourism, vacation and medical treatment.",            stay: "Up to 180 days", validity: "10 years", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "b1",       label: "B-1 Business Visa",        desc: "Business meetings and conferences.",                  stay: "Up to 180 days", validity: "10 years", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.10 },
    { id: "b1b2",     label: "B-1/B-2 Combined",         desc: "Tourism and business combined (most common).",        stay: "Up to 180 days", validity: "10 years", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "student",  label: "F-1 Student Visa",         desc: "Academic study at a US institution.",                 stay: "Duration of study", validity: "Varies", entry: "Multiple", govFeeMultiplier: 1.10, svcFeeMultiplier: 1.60 },
    { id: "transit",  label: "C-1 Transit Visa",         desc: "Transiting through US ports.",                        stay: "29 days",        validity: "Varies",   entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 0.70 },
  ],
  "hong-kong": [
    { id: "visitor",  label: "Visitor Visa (Tourist)",   desc: "Tourism and sightseeing.",                            stay: "14 days",  validity: "3 months",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "business", label: "Business Visitor Visa",    desc: "Business meetings and trade.",                        stay: "14 days",  validity: "3 months",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1.20 },
    { id: "transit",  label: "Airport Transit",          desc: "Transit without entering Hong Kong.",                 stay: "Transit",  validity: "Single",    entry: "Single",   govFeeMultiplier: 0.60, svcFeeMultiplier: 0.65 },
  ],
  egypt: [
    { id: "tourist-single", label: "Tourist E-Visa (Single)", desc: "Single entry tourism.",                         stay: "30 days",  validity: "3 months",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "tourist-multi",  label: "Tourist E-Visa (Multiple)", desc: "Multiple entry within 3 months.",             stay: "30 days",  validity: "3 months",  entry: "Multiple", govFeeMultiplier: 1.50, svcFeeMultiplier: 1.30 },
    { id: "business",       label: "Business Visa",            desc: "Business activities and meetings.",            stay: "30 days",  validity: "3 months",  entry: "Single",   govFeeMultiplier: 1.30, svcFeeMultiplier: 1.25 },
  ],
  malaysia: [
    { id: "entri",    label: "eNTRI (15 days)",          desc: "Fast 15-day single entry for tourism.",               stay: "15 days",  validity: "3 months",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "evisa",    label: "eVisa (30 days)",          desc: "30-day single entry for longer stays.",               stay: "30 days",  validity: "3 months",  entry: "Single",   govFeeMultiplier: 1.25, svcFeeMultiplier: 1.30 },
    { id: "business", label: "Business eVisa",           desc: "Business meetings and conferences.",                  stay: "30 days",  validity: "3 months",  entry: "Single",   govFeeMultiplier: 1.25, svcFeeMultiplier: 1.40 },
    { id: "student",  label: "Student Pass",             desc: "Study at Malaysian institutions.",                    stay: "Duration", validity: "Varies",    entry: "Multiple", govFeeMultiplier: 2.50, svcFeeMultiplier: 2.00 },
  ],
  singapore: [
    { id: "tourist",  label: "Visitor Pass (Tourist)",   desc: "Tourism and sightseeing.",                            stay: "30 days",  validity: "30 days",   entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "business", label: "Visitor Pass (Business)",  desc: "Business meetings and trade events.",                 stay: "30 days",  validity: "30 days",   entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1.20 },
    { id: "transit",  label: "Transit Pass",             desc: "Transit through Changi Airport.",                     stay: "96 hours", validity: "Single",    entry: "Single",   govFeeMultiplier: 0.65, svcFeeMultiplier: 0.70 },
    { id: "student",  label: "Student's Pass",           desc: "Full-time study at Singapore institutions.",          stay: "Duration", validity: "Varies",    entry: "Multiple", govFeeMultiplier: 1.35, svcFeeMultiplier: 1.80 },
  ],
  turkey: [
    { id: "tourist",  label: "Tourist E-Visa",           desc: "Tourism, leisure and cultural visits.",               stay: "30 days",  validity: "180 days",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "business", label: "Business E-Visa",          desc: "Business meetings and conferences.",                  stay: "30 days",  validity: "180 days",  entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1.20 },
    { id: "transit",  label: "Airport Transit",          desc: "Transit through Turkish airports.",                   stay: "Transit",  validity: "Single",    entry: "Single",   govFeeMultiplier: 0.50, svcFeeMultiplier: 0.60 },
  ],
  japan: [
    { id: "tourist-single", label: "Tourist Visa (Single Entry)",   desc: "Single entry for tourism.",               stay: "15–30 days", validity: "3 months", entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "tourist-multi",  label: "Tourist Visa (Multiple Entry)", desc: "Multiple entries for frequent visitors.", stay: "15 days",    validity: "3 years",  entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.80 },
    { id: "business",       label: "Business Visa",                 desc: "Business meetings and short assignments.",stay: "15–30 days", validity: "3 months", entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 1.30 },
    { id: "student",        label: "Student Visa (Ryugaku)",        desc: "Study at Japanese schools or universities.",stay:"Duration",  validity: "Varies",   entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.70 },
    { id: "work",           label: "Work Visa (Specified Activities)",desc:"Employment or designated activities.",  stay: "Duration",   validity: "Varies",   entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 2.00 },
    { id: "transit",        label: "Transit Visa",                  desc: "Transiting through Japanese airports.",   stay: "72 hours",   validity: "Single",   entry: "Single",   govFeeMultiplier: 1,    svcFeeMultiplier: 0.60 },
  ],
  canada: [
    { id: "tourist",  label: "TRV — Visitor (Tourist)",  desc: "Tourism, vacation and sightseeing.",                  stay: "Up to 6 months", validity: "10 years", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "business", label: "TRV — Business Visitor",   desc: "Business meetings and conferences.",                  stay: "Up to 6 months", validity: "10 years", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.10 },
    { id: "student",  label: "Study Permit",             desc: "Study at a Canadian DLI institution.",                stay: "Duration",       validity: "Varies",   entry: "Multiple", govFeeMultiplier: 1.76, svcFeeMultiplier: 1.80 },
    { id: "work",     label: "Work Permit",              desc: "Temporary employment in Canada.",                     stay: "Duration",       validity: "Varies",   entry: "Multiple", govFeeMultiplier: 1.76, svcFeeMultiplier: 2.00 },
    { id: "transit",  label: "Transit Visa (TRV)",       desc: "Transiting through Canadian airports.",               stay: "48 hours",       validity: "5 years",  entry: "Single",   govFeeMultiplier: 0.65, svcFeeMultiplier: 0.65 },
    { id: "medical",  label: "Medical / Super Visa",     desc: "Super Visa for parents/grandparents of PR/citizens.", stay: "Up to 5 years",  validity: "10 years", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.60 },
  ],
  australia: [
    { id: "600",      label: "Subclass 600 — Tourist",   desc: "Tourism and visiting friends or relatives.",          stay: "Up to 3 months", validity: "12 months", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "600-biz",  label: "Subclass 600 — Business",  desc: "Short-term business activities.",                     stay: "Up to 3 months", validity: "12 months", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.15 },
    { id: "500",      label: "Subclass 500 — Student",   desc: "Study at an Australian CRICOS institution.",          stay: "Duration",       validity: "Varies",   entry: "Multiple", govFeeMultiplier: 1.14, svcFeeMultiplier: 1.70 },
    { id: "485",      label: "Subclass 485 — Work",      desc: "Temporary graduate/skilled worker visa.",             stay: "2–4 years",      validity: "Varies",   entry: "Multiple", govFeeMultiplier: 1.54, svcFeeMultiplier: 2.00 },
    { id: "transit",  label: "Transit Visa (771)",       desc: "Transit through Australian international airports.",  stay: "8 hours",        validity: "Single",   entry: "Single",   govFeeMultiplier: 0,    svcFeeMultiplier: 0.55 },
    { id: "medical",  label: "Medical Treatment Visa",   desc: "Travelling to Australia for medical treatment.",      stay: "Duration",       validity: "12 months", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.30 },
  ],
  "new-zealand": [
    { id: "visitor",  label: "Visitor Visa",             desc: "Tourism and sightseeing.",                            stay: "Up to 9 months", validity: "12 months", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1 },
    { id: "business", label: "Business Visitor",         desc: "Business meetings and short-term work.",              stay: "Up to 3 months", validity: "12 months", entry: "Multiple", govFeeMultiplier: 1,    svcFeeMultiplier: 1.20 },
    { id: "student",  label: "Student Visa",             desc: "Study at a New Zealand institution.",                 stay: "Duration",       validity: "Varies",   entry: "Multiple", govFeeMultiplier: 1.22, svcFeeMultiplier: 1.60 },
    { id: "transit",  label: "Transit Visa",             desc: "Transit through New Zealand airports.",               stay: "24 hours",       validity: "Single",   entry: "Single",   govFeeMultiplier: 0,    svcFeeMultiplier: 0.55 },
  ],
  // Schengen countries — all use the same type A/C/D structure
  france:      SCHENGEN_TYPES,
  germany:     SCHENGEN_TYPES,
  switzerland: SCHENGEN_TYPES,
  spain:       SCHENGEN_TYPES,
  italy:       SCHENGEN_TYPES,
};


// ── 3. REFUND POLICY (per country) ───────────────────────────────────────────
// Add to DATA[id] in bookingdata.js as `refundPolicy`

export const REFUND_POLICIES = {
  uae:         { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Govt fee non-refundable per UAE Immigration policy." },
  thailand:    { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Thai govt fee non-refundable." },
  switzerland: { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Schengen govt fee non-refundable per consulate rules." },
  vietnam:     { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Govt fee non-refundable." },
  indonesia:   { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Govt fee non-refundable." },
  maldives:    { rejectionRefund: 100, processingFeeWaived: false, delayRefund: false, note: "Service fee refundable if documentation issues arise on our part." },
  usa:         { rejectionRefund: 100, processingFeeWaived: true, delayRefund: false, note: "100% service fee refunded on rejection. US MRV fee is strictly non-refundable per US Embassy policy." },
  "hong-kong": { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. HK Immigration fee non-refundable." },
  egypt:       { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Govt fee non-refundable." },
  malaysia:    { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Govt fee non-refundable." },
  singapore:   { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. ICA fee non-refundable." },
  turkey:      { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Govt fee non-refundable." },
  france:      { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Schengen govt fee non-refundable per consulate rules." },
  japan:       { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. No govt fee for Indians." },
  canada:      { rejectionRefund: 100, processingFeeWaived: true, delayRefund: false, note: "100% service fee refunded if visa not approved. IRCC fee strictly non-refundable." },
  australia:   { rejectionRefund: 100, processingFeeWaived: true, delayRefund: false, note: "100% service fee refunded if visa not granted. Dept of Home Affairs fee non-refundable." },
  "new-zealand":{ rejectionRefund: 100, processingFeeWaived: true, delayRefund: false, note: "100% service fee refunded if visa not granted. INZ fee non-refundable." },
  germany:     { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Schengen govt fee non-refundable per consulate rules." },
  spain:       { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Schengen govt fee non-refundable per consulate rules." },
  italy:       { rejectionRefund: 100, processingFeeWaived: true, delayRefund: true,  note: "100% service fee refunded on rejection. Schengen govt fee non-refundable per consulate rules." },
};


// ── 4. APPROVAL RATES (per country) ─────────────────────────────────────────
// Merge into DATA[id].approvalRate

export const APPROVAL_RATES = {
  uae:          97,
  thailand:     96,
  switzerland:  91,
  vietnam:      98,
  indonesia:    99,
  maldives:     99,
  usa:          82,
  "hong-kong":  93,
  egypt:        96,
  malaysia:     98,
  singapore:    92,
  turkey:       97,
  france:       90,
  japan:        88,
  canada:       79,
  australia:    84,
  "new-zealand":86,
  germany:      91,
  spain:        90,
  italy:        89,
};


/*
─────────────────────────────────────────────────────────────────────────────
HOW TO MERGE INTO bookingdata.js
─────────────────────────────────────────────────────────────────────────────

1. Import the above at the top of bookingdata.js (or just paste inline).

2. In getVisaData(), merge dynamic fields before returning:

   export function getVisaData(visaId) {
     const base = DATA[visaId] || DATA["uae"];
     const id   = DATA[visaId] ? visaId : "uae";
     return {
       ...base,
       approvalRate: APPROVAL_RATES[id] ?? 95,
       refundPolicy: REFUND_POLICIES[id] ?? REFUND_POLICIES["uae"],
       visaTypes:    VISA_TYPES[id]      ?? [],
       currency:     CURRENCY_CONFIG[id] ?? { code: "USD", symbol: "$", rate: 83.5, name: "US Dollar" },
     };
   }
─────────────────────────────────────────────────────────────────────────────
*/

// ─── GOVT FEE INCREASE PERCENTAGE (over raw embassy cost) ──────────────────
// This shows in the sidebar hover tooltip as "X% above embassy rate"
export const GOV_FEE_INCREASE_PCT = {
  uae:           2, 
  thailand:      2,
  switzerland:   2,
  vietnam:       2,
  indonesia:     2,
  maldives:      2,
  usa:           2,
  "hong-kong":   2,
  egypt:         2,
  malaysia:      2,
  singapore:     2,
  turkey:        2,
  france:        2,
  japan:         2,
  canada:        2,
  australia:     2,
  "new-zealand": 2,
  germany:       2,
  spain:         2,
  italy:         2,
};
// ─── GOVT FEE PAGE LINKS ────────────────────────────────────────────────────
// Link to your own "Govt Fee explained" page for each country
export const GOV_FEE_PAGE_LINKS = {
  uae:           "/GovtFeeHistory",
  thailand:      "/GovtFeeHistory",
  switzerland:   "/GovtFeeHistory",
  vietnam:       "/GovtFeeHistory",
  indonesia:     "/GovtFeeHistory",
  maldives:      "/GovtFeeHistory",
  usa:           "/GovtFeeHistory",
  "hong-kong":   "/GovtFeeHistory",
  egypt:         "/GovtFeeHistory",
  malaysia:      "/GovtFeeHistory",
  singapore:     "/GovtFeeHistory",
  turkey:        "/GovtFeeHistory",
  france:        "/GovtFeeHistory",
  japan:         "/GovtFeeHistory",
  canada:        "/GovtFeeHistory",
  australia:     "/GovtFeeHistory",
  "new-zealand": "/GovtFeeHistory",
  germany:       "/GovtFeeHistory",
  spain:         "/GovtFeeHistory",
  italy:         "/GovtFeeHistory",
}

// ─── REFUND JOURNEY STEP — per country, maps to Policy.js stages ─────────────
// category: which tab from Policy.js this country falls under
// stages: the specific stage IDs from POLICY_STAGES_BY_TAB that apply
export const REFUND_JOURNEY_STEP = {
  uae:           { category: "evisa",    stages: [1, 2, 3] },
  thailand:      { category: "evisa",    stages: [1, 2, 3, 5, 6] },
  vietnam:       { category: "evisa",    stages: [1, 2, 3, 5, 6] },
  indonesia:     { category: "evisa",    stages: [1, 2, 3, 5, 6] },
  egypt:         { category: "evisa",    stages: [1, 2, 3, 5, 6] },
  turkey:        { category: "evisa",    stages: [1, 2, 3, 5, 6] },
  malaysia:      { category: "evisa",    stages: [1, 2, 3, 5, 6] },
  maldives:      { category: "evisa",    stages: [1, 2, 6] },           // no delay refund
  usa:           { category: "us",       stages: [1, 2, 3, 4, 6] },    // no delay refund
  "hong-kong":   { category: "passport", stages: [1, 2, 3, 4, 6] },
  singapore:     { category: "passport", stages: [1, 2, 3, 4, 6] },
  japan:         { category: "passport", stages: [1, 2, 3, 4, 5, 6] },
  canada:        { category: "passport", stages: [1, 2, 3, 4, 6] },    // no delay refund
  australia:     { category: "passport", stages: [1, 2, 3, 4, 6] },    // no delay refund
  "new-zealand": { category: "passport", stages: [1, 2, 3, 4, 6] },    // no delay refund
  switzerland:   { category: "schengen", stages: [1, 2, 3, 4, 5, 7] },
  france:        { category: "schengen", stages: [1, 2, 3, 4, 5, 7] },
  germany:       { category: "schengen", stages: [1, 2, 3, 4, 5, 7] },
  spain:         { category: "schengen", stages: [1, 2, 3, 4, 5, 7] },
  italy:         { category: "schengen", stages: [1, 2, 3, 4, 5, 7] },
};
// Maps Policy.js stage IDs to readable bullet points per category
export const REFUND_STAGE_LABELS = {
  evisa: {
    1: { text: "Before VFS filing — 100% full refund." },
    2: {  text: "During document verification — 100% refund, not yet at VFS." },
    3: {text: "After VFS submission — VFS submission fee is non-refundable." },
    4: { text: "After biometrics — service rendered at VFS, no refund." },
    5: {  text: "Delayed past travel date — service fee waived (TheVisa Protect delay coverage)." },
    7: {  text: "Rejected by embassy — 100% service fee refunded (TheVisa Protect rejection coverage)." },
  },
  us: {
    1: { text: "Before VFS filing — 100% full refund." },
    2: {  text: "During document verification — 100% refund, not yet at VFS." },
    3: {text: "After VFS submission — VFS submission fee is non-refundable." },
    4: { text: "After biometrics — service rendered at VFS, no refund." },
    5: {  text: "Delayed past travel date — service fee waived (TheVisa Protect delay coverage)." },
    7: {  text: "Rejected by embassy — 100% service fee refunded (TheVisa Protect rejection coverage)." },
  
  },
  schengen: {
    1: { text: "Before VFS filing — 100% full refund." },
    2: {  text: "During document verification — 100% refund, not yet at VFS." },
    3: {text: "After VFS submission — VFS submission fee is non-refundable." },
    4: { text: "After biometrics — service rendered at VFS, no refund." },
    5: {  text: "Delayed past travel date — service fee waived (TheVisa Protect delay coverage)." },
    7: {  text: "Rejected by embassy — 100% service fee refunded (TheVisa Protect rejection coverage)." },
  },
  passport: {
    1: { text: "Before VFS filing — 100% full refund." },
    2: {  text: "During document verification — 100% refund, not yet at VFS." },
    3: {text: "After VFS submission — VFS submission fee is non-refundable." },
    4: { text: "After biometrics — service rendered at VFS, no refund." },
    5: {  text: "Delayed past travel date — service fee waived (TheVisa Protect delay coverage)." },
    7: {  text: "Rejected by embassy — 100% service fee refunded (TheVisa Protect rejection coverage)." },
   
  },
};

export function buildRefundStepDesc(visaId, refundPolicy) {
  const meta = REFUND_JOURNEY_STEP[visaId];
  if (!meta) return null;
  const labels = REFUND_STAGE_LABELS[meta.category];
  if (!labels) return null;

  const bullets = meta.stages
    .map((id) => labels[id])
    .filter(Boolean);

  return { bullets, note: refundPolicy?.note ?? "" };
}

// ═══════════════════════════════════════════════════════════════════════════
//  TheVisa · PDF Generator
//  Renders a styled HTML page in a hidden iframe, captures it with
//  html2canvas page-by-page, then assembles a real PDF via jsPDF.
//
//  Drop-in replacement for the old downloadBookingPdf.js.
//  No new HTML tab is opened — clicking PDF downloads a .pdf file.
// ═══════════════════════════════════════════════════════════════════════════

// ─── CDN loaders (idempotent) ─────────────────────────────────────────────
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

async function ensureLibs() {
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
  await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
}

// ─── helpers ──────────────────────────────────────────────────────────────
function fmt(n) { return Number(n).toLocaleString("en-IN"); }
function esc(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN ENTRY
// ─────────────────────────────────────────────────────────────────────────────
export async function downloadBookingPdf(data, visaId, liveState = {}) {
  await ensureLibs();

  const html = buildHtml(data, visaId, liveState);

  // ── 1. Render HTML in an offscreen iframe ────────────────────────────────
  const iframe = document.createElement("iframe");
  iframe.style.cssText = [
    "position:fixed", "top:-9999px", "left:-9999px",
    "width:1200px", "height:10px",
    "border:none", "visibility:hidden", "pointer-events:none",
    "z-index:-1",
  ].join(";");
  document.body.appendChild(iframe);

  await new Promise((res) => {
    iframe.onload = res;
    iframe.srcdoc = html;
  });

  // Wait for fonts & images inside iframe
  const iDoc = iframe.contentDocument;
  await iDoc.fonts?.ready.catch(() => {});
  await new Promise((r) => setTimeout(r, 800));

  // Measure full content height
  const iBody = iDoc.body;
  const fullH = iBody.scrollHeight;
  iframe.style.height = fullH + "px";
  await new Promise((r) => setTimeout(r, 300));

  // ── 2. Capture with html2canvas ──────────────────────────────────────────
  const canvas = await window.html2canvas(iBody, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    scrollX: 0,
    scrollY: 0,
    windowWidth: 1200,
    windowHeight: fullH,
    width: 1200,
    height: fullH,
    logging: false,
    imageTimeout: 15000,
    onclone: (clonedDoc) => {
      clonedDoc.body.style.overflow = "visible";
    },
  });

  document.body.removeChild(iframe);

  // ── 3. Build PDF ─────────────────────────────────────────────────────────
  const { jsPDF } = window.jspdf;
  const A4_W_MM = 210;
  const A4_H_MM = 297;
  const PDF_DPI = 96;

  const pxToMm = (px) => (px / 2 / PDF_DPI) * 25.4;

  const imgW_mm = A4_W_MM;
  const imgH_mm = pxToMm(canvas.height);

  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageH_mm = A4_H_MM;
  let yOffset_mm = 0;
  let pageIndex = 0;

  while (yOffset_mm < imgH_mm) {
    if (pageIndex > 0) pdf.addPage();

    const yPx = (yOffset_mm / imgH_mm) * canvas.height;
    const sliceH_px = Math.min(
      canvas.height - yPx,
      (pageH_mm / imgH_mm) * canvas.height
    );

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width  = canvas.width;
    sliceCanvas.height = sliceH_px;
    sliceCanvas.getContext("2d").drawImage(
      canvas, 0, yPx, canvas.width, sliceH_px,
              0, 0,   canvas.width, sliceH_px
    );

    const sliceH_mm = pxToMm(sliceH_px * 2);
    pdf.addImage(
      sliceCanvas.toDataURL("image/jpeg", 0.92),
      "JPEG", 0, 0, A4_W_MM, sliceH_mm
    );

    yOffset_mm += pageH_mm;
    pageIndex++;
  }

  const filename = `TheVisa_${(data?.country ?? "Visa").replace(/\s+/g, "_")}_Guide.pdf`;
  pdf.save(filename);
}

// ─────────────────────────────────────────────────────────────────────────────
//  HTML BUILDER  (self-contained, no external deps except Google Fonts)
// ─────────────────────────────────────────────────────────────────────────────
function buildHtml(data, visaId, ls) {
  const {
    displayType        = data?.visaCard?.type     ?? "Tourist",
    displayStay        = data?.visaCard?.stay     ?? "—",
    displayValidity    = data?.visaCard?.validity ?? "—",
    displayEntry       = data?.visaCard?.entry    ?? "—",
    computedGovFee     = data?.visaCard?.govFee   ?? 0,
    computedSvcFee     = data?.visaCard?.svcFee   ?? 0,
    computedSvcOrig    = data?.visaCard?.svcOrig  ?? 0,
    computedExpressFee = 0,
    computedTotal      = 0,
    expressVisa        = false,
    protectFeeRemoved  = false,
    PROTECT_FEE: _PROTECT_FEE        = PROTECT_FEE,
    PROTECT_ORIG: _PROTECT_ORIG       = PROTECT_ORIG,
  } = ls;

  const country      = data?.country      ?? "Unknown";
  const flag         = data?.flag         ?? "🌍";
  const tagline      = data?.tagline      ?? "";
  const approvalRate = data?.approvalRate ?? 95;
  const guarantee    = data?.visaCard?.guarantee ?? "";
  const reviewScore  = data?.reviewScore  ?? "4.9";
  const reviewCount  = data?.reviewCount  ?? "1,200";
  const heroImg      = data?.heroImg      ?? "";

  const journeyHtml = (data?.journeySteps ?? []).map((s, i) => `
    <div class="journey-step">
      <div class="journey-num">${s.step ?? i + 1}</div>
      <div class="journey-body">
        <div class="journey-title">${esc(s.title)}</div>
        <div class="journey-desc">${esc(s.desc)}</div>
        ${s.tag ? `<span class="journey-tag">${esc(s.tag)}</span>` : ""}
      </div>
    </div>`).join("");

  const processHtml = (data?.process ?? []).map((s, i) => `
    <div class="process-step">
      <div class="process-num">${i + 1}</div>
      <div class="process-body">
        <div class="process-title">${esc(s.title)}</div>
        <div class="process-desc">${esc(s.desc)}</div>
      </div>
    </div>`).join("");

  const reviewsHtml = (data?.reviews ?? []).map(r => `
    <div class="review-card">
      <div class="review-top">
        <div class="review-avatar">${r.name.charAt(0)}</div>
        <div class="review-info">
          <div class="review-name">${esc(r.name)}</div>
          <div class="review-date">${esc(r.date)}</div>
        </div>
        <div class="review-stars">${"★".repeat(r.rating)}${"☆".repeat(5 - r.rating)}</div>
      </div>
      <div class="review-title">"${esc(r.title)}"</div>
      <div class="review-body">${esc(r.body)}</div>
    </div>`).join("");

  const faqsHtml = (data?.faqs ?? []).map(f => `
    <div class="faq-item">
      <div class="faq-q"><span class="faq-icon">Q</span>${esc(f.q)}</div>
      <div class="faq-a">${esc(f.a)}</div>
    </div>`).join("");

  const destHtml = (data?.destinations ?? []).map(d => `
    <div class="dest-card">
      <img src="${d.img}" alt="${esc(d.name)}" crossorigin="anonymous" />
      <div class="dest-overlay"></div>
      <span class="dest-name">${esc(d.name)}</span>
    </div>`).join("");

  const logoSvg = `
    <svg width="140" height="40" viewBox="0 0 140 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g transform="translate(2,8)">
        <ellipse cx="14" cy="12" rx="11" ry="4" fill="rgb(34,83,120)" opacity="0.85"/>
        <path d="M25 11 Q30 12 25 13Z" fill="rgb(34,83,120)" opacity="0.85"/>
        <path d="M3 12 Q0 8 3 4 L7 8Z" fill="rgb(34,83,120)" opacity="0.55"/>
        <path d="M13 9 L9 2 L20 6 L21 9Z" fill="rgb(34,83,120)" opacity="0.65"/>
        <path d="M13 15 L9 22 L20 18 L21 15Z" fill="rgb(34,83,120)" opacity="0.65"/>
      </g>
      <text x="36" y="22"
        font-family="PT Serif, Georgia, serif"
        font-size="16" font-weight="600"
        fill="rgb(34,83,120)"
        letter-spacing="-0.2">TheVisaCo</text>
      <text x="36" y="33"
        font-family="DM Sans, Arial, sans-serif"
        font-size="7" font-weight="700"
        fill="rgb(34,83,120)"
        letter-spacing="1.5">ZERO WORRY VISA</text>
    </svg>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>TheVisa · ${country} Visa Guide</title>
<link rel="preconnect" href="https://fonts.googleapis.com"/>
<link href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;600;700&family=PT+Serif:ital,wght@0,400;0,700;1,400&display=swap" rel="stylesheet"/>
<style>
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{
  font-family:'DM Sans',sans-serif;
  background:#fff;color:#0a0a0a;
  -webkit-font-smoothing:antialiased;
  line-height:1.6;
  width:1200px;
}
a{text-decoration:none;color:inherit}
img{display:block;max-width:100%}

:root{
  --navy:#0d1b4b;--navy-mid:#162259;--navy-lt:#1e3a8a;--navy-faint:#e8edf8;
  --accent:#1e3a8a;--green:#065f46;--green-bg:#ecfdf5;--green-bd:#a7f3d0;
  --border:#e4e4ef;--border-2:#d0d0e8;--surface-2:#f8f9ff;
  --ink-soft:#4a4a6a;--ink-faint:#8888a8;--gold:#c9a84c;
  --ff:'PT Serif',Georgia,serif;
  --ff2:'Plus Jakarta Sans',sans-serif;
  --ff3:'DM Sans',sans-serif;
}

.page-wrap{max-width:1100px;margin:0 auto;padding:0 2rem 4rem}

.pdf-header{
  display:flex;align-items:center;justify-content:space-between;
  padding:.75rem 2rem;
  border-bottom:1px solid var(--border);
  background:#fff;
}
.pdf-header__right{
  font-family:var(--ff2);font-size:.7rem;font-weight:600;
  color:var(--ink-faint);letter-spacing:.04em;
}

.hero{
  position:relative;min-height:380px;border-radius:20px;overflow:hidden;
  margin:1.25rem 0 2rem;display:flex;align-items:flex-end;
}
.hero-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;object-position:center 35%;filter:brightness(.38) saturate(1.1)}
.hero-overlay{position:absolute;inset:0;background:linear-gradient(120deg,rgba(13,27,75,.88) 0%,rgba(13,27,75,.5) 60%,rgba(13,27,75,.3) 100%)}
.hero-content{position:relative;z-index:2;padding:2.5rem;max-width:680px}
.hero-eyebrow{display:inline-block;background:rgba(255,255,255,.12);color:rgba(255,255,255,.9);font-family:var(--ff2);font-size:.68rem;font-weight:700;letter-spacing:.1em;text-transform:uppercase;padding:.28rem 1rem;border-radius:4px;margin-bottom:.65rem}
.hero-title{font-family:var(--ff);font-size:2.8rem;font-weight:700;color:#fff;letter-spacing:-.02em;line-height:1.1;margin-bottom:.5rem}
.hero-tagline{font-size:.9rem;color:rgba(255,255,255,.7);line-height:1.7;max-width:500px}
.hero-badge{position:absolute;top:1.5rem;right:1.75rem;z-index:3;background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);border-radius:10px;padding:.75rem 1.25rem;text-align:center}
.hero-badge-rate{font-family:var(--ff2);font-size:1.8rem;font-weight:800;color:#4cde6a;line-height:1}
.hero-badge-label{font-family:var(--ff2);font-size:.68rem;font-weight:600;color:rgba(255,255,255,.8);margin-top:.15rem}
.hero-badge-guarantee{font-family:var(--ff2);font-size:.62rem;color:rgba(255,255,255,.6);margin-top:.3rem}

.main-grid{display:grid;grid-template-columns:1fr 340px;gap:2.25rem;align-items:start;margin-top:1.25rem}

.section-label{font-family:var(--ff2);font-size:.66rem;font-weight:700;color:var(--navy);text-transform:uppercase;letter-spacing:.12em;margin-bottom:.35rem}
.section-title{font-family:var(--ff);font-size:1.6rem;font-weight:700;color:var(--navy);letter-spacing:-.02em;line-height:1.2;margin-bottom:1.25rem}

.info-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.7rem;margin-bottom:.9rem}
.info-card{background:var(--surface-2);border:1.5px solid var(--border);border-radius:12px;padding:1rem}
.info-card__label{font-family:var(--ff3);font-size:.66rem;font-weight:600;color:var(--ink-faint);text-transform:uppercase;letter-spacing:.08em;margin-bottom:.35rem}
.info-card__val{font-family:var(--ff2);font-size:.95rem;font-weight:700;color:#0a0a0a}

.guarantee-strip{display:flex;align-items:center;gap:.55rem;background:var(--navy-faint);border:1px solid var(--border-2);border-radius:9px;padding:.6rem .9rem;margin-bottom:1.75rem}
.guarantee-strip__text{font-family:var(--ff2);font-size:.78rem;font-weight:700;color:var(--navy);flex:1}
.guarantee-strip__rate{font-family:var(--ff2);font-size:.72rem;font-weight:700;color:var(--green);background:var(--green-bg);border:1px solid var(--green-bd);border-radius:5px;padding:2px 9px}

.journey{display:flex;flex-direction:column;gap:0;position:relative}
.journey-step{display:flex;gap:18px;position:relative;padding-bottom:24px}
.journey-step:not(:last-child)::before{content:"";position:absolute;top:42px;left:20px;width:1.5px;bottom:0;background:var(--border-2)}
.journey-num{width:42px;height:42px;border-radius:50%;border:1.5px solid var(--border-2);background:#fff;display:flex;align-items:center;justify-content:center;font-family:var(--ff2);font-size:11px;font-weight:800;color:var(--ink-soft);flex-shrink:0;position:relative;z-index:1}
.journey-body{padding-top:9px;flex:1}
.journey-title{font-family:var(--ff2);font-size:13px;font-weight:700;color:#0a0a0a;margin-bottom:4px}
.journey-desc{font-family:var(--ff3);font-size:12px;color:var(--ink-soft);line-height:1.65}
.journey-tag{display:inline-block;margin-top:7px;font-family:var(--ff2);font-size:10px;font-weight:700;color:var(--accent);background:var(--navy-faint);border:1px solid var(--border-2);border-radius:4px;padding:3px 8px}

.essentials-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:.9rem;margin-bottom:1.75rem}
.ess-card{display:flex;flex-direction:column;align-items:center;gap:.45rem;background:var(--surface-2);border:1px solid var(--border);border-radius:12px;padding:1.1rem .65rem .8rem}
.ess-card svg{width:100%;max-width:80px;height:auto}
.ess-title{font-family:var(--ff2);font-size:.78rem;font-weight:700;color:#0a0a0a;text-align:center}

.process-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.9rem;margin-bottom:1.75rem}
.process-step{display:flex;gap:.9rem;background:var(--surface-2);border:1px solid var(--border);border-radius:12px;padding:1rem}
.process-num{width:34px;height:34px;border-radius:9px;background:var(--navy);color:#fff;font-family:var(--ff2);font-size:.76rem;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.process-title{font-family:var(--ff2);font-size:.84rem;font-weight:700;color:#0a0a0a;margin-bottom:3px}
.process-desc{font-family:var(--ff3);font-size:.74rem;color:var(--ink-soft);line-height:1.55}

.dest-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:.55rem;margin-bottom:1.75rem}
.dest-card{position:relative;border-radius:11px;overflow:hidden;min-height:110px}
.dest-card:first-child{grid-column:span 2;min-height:150px}
.dest-card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.dest-overlay{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,.65) 0%,transparent 55%)}
.dest-name{position:absolute;bottom:9px;left:11px;font-family:var(--ff2);font-size:.76rem;font-weight:700;color:#fff;text-shadow:0 1px 5px rgba(0,0,0,.5)}

.reviews-score-bar{display:flex;align-items:center;gap:.9rem;margin-bottom:1.25rem}
.reviews-score__num{font-family:var(--ff);font-size:2.2rem;font-weight:700;line-height:1}
.reviews-score__stars{color:#f59e0b;font-size:.85rem}
.reviews-score__count{font-family:var(--ff3);font-size:.68rem;color:var(--ink-soft)}
.reviews-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:.75rem;margin-bottom:1.75rem}
.review-card{background:#fff;border:1px solid var(--border);border-radius:12px;padding:1rem}
.review-top{display:flex;align-items:center;gap:.6rem;margin-bottom:.75rem}
.review-avatar{width:32px;height:32px;border-radius:50%;background:var(--navy-mid);color:#fff;font-family:var(--ff2);font-size:.76rem;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0}
.review-name{font-family:var(--ff2);font-size:.78rem;font-weight:700}
.review-date{font-family:var(--ff3);font-size:.65rem;color:var(--ink-faint)}
.review-stars{margin-left:auto;color:#f59e0b;font-size:.76rem}
.review-title{font-family:var(--ff2);font-size:.78rem;font-weight:700;margin-bottom:.3rem}
.review-body{font-family:var(--ff3);font-size:.72rem;color:var(--ink-soft);line-height:1.65}

.faq-list{display:flex;flex-direction:column;gap:.7rem;margin-bottom:1.75rem}
.faq-item{background:#fff;border:1px solid var(--border);border-radius:11px;padding:1rem 1.1rem}
.faq-q{display:flex;align-items:flex-start;gap:.6rem;font-family:var(--ff2);font-size:.84rem;font-weight:700;color:#0a0a0a;margin-bottom:.55rem}
.faq-icon{background:var(--navy);color:#fff;font-family:var(--ff2);font-size:.65rem;font-weight:800;width:19px;height:19px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
.faq-a{font-family:var(--ff3);font-size:.76rem;color:var(--ink-soft);line-height:1.72;padding-left:1.5rem}

.sidebar{background:#fff;border:1px solid var(--border);border-radius:14px;overflow:hidden;position:sticky;top:80px}
.sb-topbar{background:var(--navy-faint);border-bottom:1px solid var(--border);padding:.6rem 1.1rem;display:flex;align-items:center;justify-content:space-between;gap:.55rem}
.sb-guarantee{font-family:var(--ff2);font-size:.68rem;font-weight:600;color:var(--navy)}
.sb-badge{font-family:var(--ff2);font-size:.62rem;font-weight:600;background:var(--navy);color:#fff;padding:.16rem .65rem;border-radius:4px}
.sb-body{padding:1.1rem;display:flex;flex-direction:column;gap:.9rem}
.sb-price-main{font-family:var(--ff2);font-size:2.2rem;font-weight:800;letter-spacing:-.04em;text-align:center}
.sb-price-sub{font-family:var(--ff2);font-size:.62rem;text-transform:uppercase;letter-spacing:.1em;color:var(--ink-soft);text-align:center;margin-top:2px}
.sb-price-note{font-family:var(--ff3);font-size:.65rem;color:var(--ink-faint);text-align:center;margin-top:.2rem}
.sb-cta{display:flex;align-items:center;justify-content:center;width:80%;margin:0 auto;background:var(--navy-mid);color:#fff;font-family:var(--ff2);font-size:.84rem;font-weight:700;padding:.7rem 1.4rem;border-radius:999px;text-align:center}
.sb-fees{display:flex;flex-direction:column;gap:.5rem}
.fee-row{display:flex;align-items:flex-start;justify-content:space-between;gap:.45rem}
.fee-name{font-family:var(--ff2);font-size:.8rem;font-weight:600;color:#1a1a2e}
.fee-note{font-family:var(--ff3);font-size:.62rem;color:var(--ink-faint)}
.fee-amount{font-family:var(--ff2);font-size:.82rem;font-weight:700;white-space:nowrap}
.fee-strike{text-decoration:line-through;color:var(--ink-faint);font-size:.72rem}
.fee-green{color:var(--green)}
.fee-total{font-size:.95rem}
.fee-divider{height:1px;background:#000;margin:.35rem 0}
.fee-bold{font-weight:800}
.sb-trust{display:flex;justify-content:center;gap:1rem;padding-top:.4rem;flex-wrap:wrap}
.sb-trust-item{font-family:var(--ff2);font-size:.62rem;font-weight:600;color:var(--ink-soft)}

.cta-block{background:linear-gradient(135deg,var(--navy) 0%,var(--navy-lt) 100%);border-radius:18px;padding:2.5rem;text-align:center;margin-top:1.75rem}
.cta-block__title{font-family:var(--ff);font-size:1.6rem;font-weight:700;color:#fff;margin-bottom:.45rem}
.cta-block__sub{font-family:var(--ff3);font-size:.85rem;color:rgba(255,255,255,.8);margin-bottom:1.25rem}
.cta-block .btn{display:inline-block;background:#fff;color:var(--navy);font-family:var(--ff2);font-size:.85rem;font-weight:700;padding:.7rem 2rem;border-radius:8px}

.footer{border-top:1px solid var(--border);margin-top:2.5rem;padding-top:1.25rem;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:.75rem}
.footer-logo{font-family:var(--ff2);font-size:.85rem;font-weight:800;color:var(--navy)}
.footer-text{font-family:var(--ff3);font-size:.68rem;color:var(--ink-faint)}

.section{margin-top:2.25rem}
</style>
</head>
<body>

<!-- ── PDF HEADER WITH LOGO ── -->
<div class="pdf-header">
  ${logoSvg}
  <div class="pdf-header__right">${flag} ${esc(country)} Visa Guide · ${new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })}</div>
</div>

<div class="page-wrap">

  <!-- ── HERO ── -->
  <section class="hero">
    ${heroImg
      ? `<img class="hero-img" src="${heroImg}" alt="${esc(country)}" crossorigin="anonymous"/>`
      : `<div style="position:absolute;inset:0;background:linear-gradient(135deg,#0d1b4b,#1e3a8a)"></div>`}
    <div class="hero-overlay"></div>
    <div class="hero-content">
      <div class="hero-eyebrow">${flag} ${esc(country)} Visa</div>
      <h1 class="hero-title">${esc(country)}</h1>
      <p class="hero-tagline">${esc(tagline)}</p>
    </div>
    <div class="hero-badge">
      <div class="hero-badge-rate">${approvalRate}%</div>
      <div class="hero-badge-label">Approval Rate</div>
      <div class="hero-badge-guarantee">${esc(guarantee)}</div>
    </div>
  </section>

  <!-- ── MAIN GRID ── -->
  <div class="main-grid">
    <main>

      <!-- INFO -->
      <section class="section">
        <div class="section-label">Visa Info</div>
        <div class="section-title">${esc(country)} Visa Details</div>
        <div class="info-grid">
          <div class="info-card"><div class="info-card__label">Visa Type</div><div class="info-card__val">${esc(displayType)}</div></div>
          <div class="info-card"><div class="info-card__label">Stay Duration</div><div class="info-card__val">${esc(displayStay)}</div></div>
          <div class="info-card"><div class="info-card__label">Validity</div><div class="info-card__val">${esc(displayValidity)}</div></div>
          <div class="info-card"><div class="info-card__label">Entry Type</div><div class="info-card__val">${esc(displayEntry)}</div></div>
        </div>
        <div class="guarantee-strip">
          <span>⚡</span>
          <span class="guarantee-strip__text">${esc(guarantee)}</span>
          <span class="guarantee-strip__rate">${approvalRate}% Approval Rate</span>
        </div>
      </section>

      <!-- JOURNEY -->
      <section class="section">
        <div class="section-label">Step-by-Step Process</div>
        <div class="section-title">How the ${esc(country)} Visa Works</div>
        <div class="journey">${journeyHtml}</div>
      </section>

      <!-- ESSENTIALS -->
      <section class="section">
        <div class="section-label">Required Documents</div>
        <div class="section-title">What You'll Need</div>
        <div class="essentials-grid">
          <div class="ess-card">
            <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="20" y="10" width="180" height="120" rx="10" fill="#1a2744"/>
              <rect x="20" y="10" width="180" height="30" rx="10" fill="#243260"/>
              <rect x="20" y="30" width="180" height="10" fill="#243260"/>
              <circle cx="110" cy="55" r="16" fill="none" stroke="#c9a84c" stroke-width="1.2"/>
              <circle cx="110" cy="55" r="11" fill="none" stroke="#c9a84c" stroke-width="0.7"/>
              <ellipse cx="110" cy="55" rx="6" ry="16" fill="none" stroke="#c9a84c" stroke-width="0.7"/>
              <line x1="94" y1="55" x2="126" y2="55" stroke="#c9a84c" stroke-width="0.7"/>
              <text x="110" y="27" text-anchor="middle" fill="#c9a84c" font-size="8" font-family="serif" letter-spacing="2">PASSPORT</text>
            </svg>
            <div class="ess-title">Passport</div>
          </div>
          <div class="ess-card">
            <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="10" y="18" width="200" height="104" rx="10" fill="#0d1e4a"/>
              <rect x="10" y="18" width="200" height="26" rx="10" fill="#c9a84c"/>
              <rect x="10" y="34" width="200" height="10" fill="#c9a84c"/>
              <text x="20" y="31" fill="#0d1e4a" font-size="7.5" font-family="monospace" font-weight="700" letter-spacing="1.5">AIR INDIA</text>
              <text x="20" y="64" fill="#c9a84c" font-size="20" font-family="monospace" font-weight="700">DEL  CDG</text>
            </svg>
            <div class="ess-title">Return Ticket</div>
          </div>
          <div class="ess-card">
            <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="20" y="30" width="180" height="104" rx="4" fill="#fafaf7" stroke="#e0dbd0" stroke-width="0.8"/>
              <rect x="20" y="20" width="180" height="22" rx="4" fill="#1a2744"/>
              <rect x="20" y="40" width="144" height="10" fill="#1a2744"/>
              <text x="40" y="38" fill="#c9a84c" font-size="7.5" font-family="monospace" font-weight="700" letter-spacing="1">BANK STATEMENT</text>
            </svg>
            <div class="ess-title">Bank Statement</div>
          </div>
          <div class="ess-card">
            <svg viewBox="0 0 220 140" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M110 18 L152 34 L152 76 C152 100 134 118 110 126 C86 118 68 100 68 76 L68 34 Z" fill="#1a2744"/>
              <path d="M110 24 L146 38 L146 76 C146 97 130 113 110 120 C90 113 74 97 74 76 L74 38 Z" fill="#243260"/>
              <path d="M96 72 L106 82 L126 60" stroke="#c9a84c" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <div class="ess-title">Travel Insurance</div>
          </div>
        </div>
      </section>

    </main>

    <!-- ── SIDEBAR ── -->
    <aside>
      <div class="sidebar">
        <div class="sb-topbar">
          <div class="sb-guarantee">⚡ ${esc(guarantee)}</div>
          <span class="sb-badge">${esc(data?.visaCard?.badge ?? "Trusted")}</span>
        </div>
        <div class="sb-body">
          <div>
            <div class="sb-price-main">₹${fmt(computedTotal)}</div>
            <div class="sb-price-sub">Total per person</div>
            <div class="sb-price-note">${expressVisa ? "Govt fee + service fee + protection + express" : "Govt fee + service fee + protection included"}</div>
          </div>
          <div class="sb-cta">Start Application</div>
          <div class="sb-fees">
            <div class="fee-row">
              <div><div class="fee-name">Govt Fee</div><div class="fee-note">Paid upfront · local currency</div></div>
              <span class="fee-amount">${computedGovFee === 0 ? '<span style="color:var(--green)">FREE</span>' : `₹${fmt(computedGovFee)}`}</span>
            </div>
            <div class="fee-row">
              <div><div class="fee-name">Service Fee</div></div>
              <div style="display:flex;align-items:center;gap:5px">
                <span class="fee-amount fee-strike">₹${fmt(computedSvcOrig)}</span>
                <span class="fee-amount">₹${fmt(computedSvcFee)}</span>
              </div>
            </div>
            ${expressVisa ? `
            <div class="fee-row">
              <div><div class="fee-name">Express Processing</div></div>
              <span class="fee-amount" style="color:var(--navy)">+₹${fmt(computedExpressFee)}</span>
            </div>` : ""}
            <div class="fee-row">
              <div><div class="fee-name">Visa Protect</div></div>
              <div style="display:flex;align-items:center;gap:5px">
                <span class="fee-amount fee-strike">₹${fmt(_PROTECT_ORIG)}</span>
                ${protectFeeRemoved
                  ? `<span class="fee-amount fee-green">FREE</span>`
                  : `<span class="fee-amount">₹${fmt(_PROTECT_FEE)}</span>`}
              </div>
            </div>
            <div class="fee-divider"></div>
            <div class="fee-row">
              <span class="fee-name fee-bold">Total</span>
              <span class="fee-amount fee-total">₹${fmt(computedTotal)}</span>
            </div>
          </div>
          <div class="sb-trust">
            <div class="sb-trust-item">✓ Secure</div>
            <div class="sb-trust-item">✓ Verified</div>
            <div class="sb-trust-item">✓ Trusted</div>
          </div>
        </div>
      </div>
    </aside>
  </div>

  <!-- ── PROCESS ── -->
  <section class="section">
    <div class="section-label">How It Works</div>
    <div class="section-title">4-Step Process With Us</div>
    <div class="process-grid">${processHtml}</div>
  </section>

  ${destHtml ? `
  <!-- ── DESTINATIONS ── -->
  <section class="section">
    <div class="section-label">Popular Places</div>
    <div class="section-title">Top Spots in ${esc(country)}</div>
    <div class="dest-grid">${destHtml}</div>
  </section>` : ""}

  <!-- ── REVIEWS ── -->
  <section class="section">
    <div class="section-label">Customer Reviews</div>
    <div class="section-title">What Our Customers Say</div>
    <div class="reviews-score-bar">
      <div class="reviews-score__num">${reviewScore}</div>
      <div>
        <div class="reviews-score__stars">★★★★★</div>
        <div class="reviews-score__count">${reviewCount} verified reviews</div>
      </div>
    </div>
    <div class="reviews-grid">${reviewsHtml}</div>
  </section>

  <!-- ── FAQ ── -->
  <section class="section">
    <div class="section-label">${esc(country)} FAQ</div>
    <div class="section-title">Frequently Asked Questions</div>
    <div class="faq-list">${faqsHtml}</div>
  </section>

  <!-- ── CTA BLOCK ── -->
  <div class="cta-block">
    <div class="cta-block__title">Ready to Visit ${esc(country)}?</div>
    <div class="cta-block__sub">Join 50,000+ Indians who trust TheVisa for their travel documents.</div>
    <span class="btn">Apply for ${esc(country)} Visa </span>
  </div>

  <!-- ── FOOTER ── -->
  <footer class="footer">
    <div class="footer-logo">✈ TheVisaCo</div>
    <div class="footer-text">© ${new Date().getFullYear()} TheVisaCo · All rights reserved · Zero Worry Visa</div>
  </footer>

</div>
</body>
</html>`;
}

